// Copyright (c) 2026 AesirX.io Ltd. All rights reserved.
// This source code is proprietary and confidential.
// See LICENSE.md for licensing terms.
/**
 * Audit event action types for Sprint 1.0.0 + 1.1.0.
 * See: docs/compliance/audit-event-taxonomy.md
 */
export const AuditActionType = {
    // Case Lifecycle
    CASE_CREATED: 'case_created',
    CASE_UPDATED: 'case_updated',
    // Sprint 63.40.0 — an applicant statement was answered, changed or cleared.
    FORM_DOSSIER_STATEMENT_CHANGED: 'form_dossier_statement_changed',
    STATUS_CHANGED: 'status_changed',
    SUBSTATUS_CHANGED: 'substatus_changed',
    ASSIGNMENT_CHANGED: 'assignment_changed',
    CASE_CLOSED: 'case_closed',
    CASE_REJECTED: 'case_rejected',
    // Evidence
    NOTE_ADDED: 'note_added',
    ATTACHMENT_UPLOADED: 'attachment_uploaded',
    ATTACHMENT_DOWNLOADED: 'attachment_downloaded',
    /**
     * Sprint 64.36.0 — a **refused** retrieval of an attachment, denied by the
     * owner-type / export-kind download policy Sprint 64.0.0 introduced.
     *
     * A first-class code rather than a failed `ATTACHMENT_DOWNLOADED`, for the
     * reason `REGULATORY_ARTIFACT_ACCESS_DENIED` is one further down this file:
     * the question an inspection asks is not who retrieved a filing-grade
     * artefact, it is who **tried**. 64.0.0 shipped the denial class and nothing
     * wrote it anywhere, so a principal could enumerate the org's packs, identity
     * dumps and credential exports and leave no row behind — while a denied
     * *upload* has recorded `quota_breached` since 41.4.0.
     *
     * Emitted on the generic attachment routes only
     * (`/api/v1/attachments/...`), which is where their success events already
     * go. The operator-side platform-files routes emit the same facts into
     * `grc_platform_file_audit` under `owner_type_download_denied`, alongside
     * their own `downloaded`. Two ledgers, one event shape — see
     * `modules/attachments/download-denial-audit.ts`.
     */
    ATTACHMENT_DOWNLOAD_DENIED: 'attachment_download_denied',
    // SLA
    SLA_PAUSED: 'sla_paused',
    SLA_RESUMED: 'sla_resumed',
    /**
     * fix-sprint #447 W9 (owner ruling OR3) — a person changed whether a
     * processor or third party must act on a rights request after it was
     * created. Recorded with the old and new value and the old and new due
     * dates: the change re-dates the case from receipt against the installed
     * record whose `coordination_mode` matches.
     */
    THIRD_PARTY_INVOLVEMENT_CHANGED: 'third_party_involvement_changed',
    /**
     * Sprint 63.31.0 (#413, owner ruling Q1 (a), decision E3) — the daily
     * rights-deadline sweep dated a case whose only installed clock was not yet
     * in force when the request was received, on the day the clock came into
     * force (`deadline_state: awaiting_activation → dated`). Recorded with the
     * interim due date and its source, and the statutory due date and its record.
     */
    RIGHTS_DEADLINE_DATED_ON_ACTIVATION: 'rights_deadline_dated_on_activation',
    // Admin Config
    NOTIFICATION_SETTINGS_UPDATED: 'notification_settings_updated',
    /**
     * Sprint 62.45.0 (AC17) — the organisation's working days or holidays were
     * saved through `PATCH /settings/calendar`. Recorded with the prior and the
     * new calendar: the change moves every working-day deadline the platform
     * computes for the organisation afterwards, so who moved them is asked.
     */
    ORG_CALENDAR_UPDATED: 'org_calendar_updated',
    /**
     * Sprint 62.55.0 (AC-C1, OQ-86-5) — the organisation's financial-year end was
     * saved through `PATCH /settings/financial-year`. Recorded with the prior and
     * the new end: it moves every usage-notification window derived for the
     * organisation afterwards.
     */
    ORG_FINANCIAL_YEAR_UPDATED: 'org_financial_year_updated',
    STATUTORY_CALENDAR_UPDATED: 'statutory_calendar_updated',
    /**
     * Sprint 63.37.0 (E0) — Settings › Organisation identity was saved through
     * `PATCH /organizations/:orgId/identity`. Recorded with the prior and the
     * saved identity: official forms are filled from it.
     */
    ORG_IDENTITY_UPDATED: 'org_identity_updated',
    /**
     * Sprint 63.37.0 (E0) — the start-up migration copied the organisation's
     * branding name, address and registration number into an empty identity.
     * One row per organisation it wrote.
     */
    ORG_IDENTITY_COPIED_FROM_BRANDING: 'org_identity_copied_from_branding',
    /**
     * fix-sprint #431 — Settings › Request Types (`PATCH /settings/intake`)
     * was saved. The save changed which rights requests the public form offers
     * and wrote no audit entry. Recorded with the prior and the saved settings.
     */
    INTAKE_SETTINGS_UPDATED: 'intake_settings_updated',
    /**
     * fix-sprint #431 — Settings › SLA Deadlines (`PATCH /sla/config`) was
     * saved: the configured periods, the warning threshold or the automatic
     * escalation switch. It wrote no audit entry. Recorded with the prior and
     * the saved configuration.
     */
    SLA_CONFIG_UPDATED: 'sla_config_updated',
    // Auth (emitted by auth module)
    LOGIN_SUCCESS: 'login_success',
    LOGIN_FAILURE: 'login_failure',
    LOGOUT: 'logout',
    SESSION_EXPIRED: 'session_expired',
    // Sprint 1.1.0 additions
    ATTACHMENT_DELETED: 'attachment_deleted',
    /**
     * Sprint 63.1.0 — one aggregate row per access-log retention sweep run.
     *
     * **One row per run, never one per destroyed row.** That is Sprint 63.0.1's
     * own decision 3 applied to the other end of the lifecycle: the middleware
     * refuses to double-write an audit row per access, and a sweep that wrote one
     * per destruction would double the volume the sweep exists to reduce. The
     * aggregate carries the count, the clock reading, the window in force, the
     * per-bucket retention reasons and who or what ran it, which is exactly the
     * six facts AC12 requires an auditor to be able to answer without a database
     * client.
     */
    ACCESS_LOG_RETENTION_SWEPT: 'access_log_retention_swept',
    /**
     * Sprint 63.1.0 — one aggregate row per abandoned-upload sweep run.
     *
     * The per-file record is the `ATTACHMENT_DELETED` row `softDelete` already
     * writes, which names the file, the owner type and the actor. This row is the
     * run: its clock reading, its grace window and its counts.
     */
    ABANDONED_UPLOAD_SWEPT: 'abandoned_upload_swept',
    /**
     * Sprint 63.21.0 (Q4) — one aggregate row per staged-discovery-record
     * retention sweep run. The per-record account is a destruction event on the
     * deletion proof chain and an entry in the discovery audit trail; this row
     * is the run: its clock reading and its counts.
     */
    STAGED_RECORD_RETENTION_SWEPT: 'staged_record_retention_swept',
    /**
     * Sprint 63.21.0 (Q5, fix-sprint 63.13.0 F2) — one aggregate row per
     * metric-series retention sweep run, written before any row is stamped for
     * the TTL monitor.
     */
    METRIC_SERIES_RETENTION_SWEPT: 'metric_series_retention_swept',
    REFERENCE_ADDED: 'reference_added',
    CHECKLIST_UPDATED: 'checklist_updated',
    IDENTITY_VERIFIED: 'identity_verified',
    CLOSURE_SUMMARY_ADDED: 'closure_summary_added',
    APPROVAL_GRANTED: 'approval_granted',
    TEMPLATE_APPLIED: 'template_applied',
    EXPORT_GENERATED: 'export_generated',
    SLA_WARNING: 'sla_warning',
    SLA_OVERDUE: 'sla_overdue',
    SLA_ESCALATION_CHANGED: 'sla_escalation_changed',
    SLA_BREACHED: 'sla_breached',
    /**
     * fix-sprint-20260927-0225 F2 (remediation A) — a startup migration revisited
     * an SLA breach or escalation flag the SLA engine wrote under an earlier rule:
     * it cleared the flag, or kept it and corrected the time it was set. Its own
     * code, never `sla_breached` / `sla_escalation_changed`, so a case timeline
     * does not show a clearing as a breach. `new_value.flag` names the flag.
     */
    SLA_FLAG_REVISITED: 'sla_flag_revisited',
    TEMPLATE_CREATED: 'template_created',
    TEMPLATE_UPDATED: 'template_updated',
    TEMPLATE_DELETED: 'template_deleted',
    // Fulfillment (Sprint #1216)
    FULFILLMENT_ASSIGNED: 'fulfillment_assigned',
    FULFILLMENT_TASK_UPDATED: 'fulfillment_task_updated',
    FULFILLMENT_SIGNOFF: 'fulfillment_signoff',
    FULFILLMENT_TASK_DELETED: 'fulfillment_task_deleted',
    RESPONSE_GENERATED: 'response_generated',
    RESPONSE_SENT: 'response_sent',
    EXTENSION_REQUESTED: 'extension_requested',
    EXTENSION_DECISION: 'extension_decision',
    // Sprint 43.1.0 E9 — Final audit taxonomy gaps
    // Emitted by Sprint 43.1.0 batch 2 (E2 ResponseDelivery) when a
    // rendered response is dispatched via email/portal/postal/in-person.
    RESPONSE_DELIVERED: 'response_delivered',
    // Emitted by Sprint 43.1.0 batch 1 (E8 CrossBorderDetector) when a
    // DSR triggers the Decree 13 / PDPL Art 25 cross-border dossier flow.
    CROSS_BORDER_DOSSIER_TRIGGERED: 'cross_border_dossier_triggered',
    // Sprint 43.2.0 batch 1 — Admin & Retention & Breach events
    //
    // Response-template events REUSE the existing TEMPLATE_CREATED /
    // TEMPLATE_UPDATED / TEMPLATE_DELETED constants above (1.1.0). The E1
    // admin surface emits TEMPLATE_DELETED with `metadata.deactivated =
    // true` to preserve soft-delete semantics without adding a duplicate
    // event type. No new template_* entries are added here.
    //
    // Department admin (E2) — new first-class events since the 1.1.0
    // family did not cover department lifecycle or membership.
    DEPARTMENT_CREATED: 'department_created',
    DEPARTMENT_RENAMED: 'department_renamed',
    DEPARTMENT_DEACTIVATED: 'department_deactivated',
    DEPARTMENT_MEMBER_ADDED: 'department_member_added',
    DEPARTMENT_MEMBER_REMOVED: 'department_member_removed',
    // Retention policy (E4) — CRUD on `grc_retention_policies` plus
    // per-attachment stamping on identity-proof uploads.
    RETENTION_POLICY_CREATED: 'retention_policy_created',
    RETENTION_POLICY_UPDATED: 'retention_policy_updated',
    RETENTION_APPLIED: 'retention_applied',
    // Breach trigger (E5) — operator flags a DSR case as breach-causing,
    // which creates a `grc_data_breaches` record and starts the 72h Mẫu
    // số 08 filing clock via CrossBorderFilingService.
    DATA_BREACH_DETECTED: 'data_breach_detected',
    MPS_FILING_STARTED: 'mps_filing_started',
    // ── Sprint 47.5.0 — Forseti AI Legal Advisor (foundation) ─────────
    // Family plan §3.3 + §17. Codes registered here at foundation; emission
    // begins in 47.5.1+ as each module is implemented.
    // AI Advisor chat surface (47.5.4)
    AI_ADVISOR_CHAT_CREATED: 'ai_advisor_chat_created',
    AI_ADVISOR_CHAT_RENAMED: 'ai_advisor_chat_renamed',
    AI_ADVISOR_CHAT_DELETED: 'ai_advisor_chat_deleted',
    AI_ADVISOR_MESSAGE_SENT: 'ai_advisor_message_sent',
    AI_ADVISOR_MESSAGE_STREAMED: 'ai_advisor_message_streamed',
    AI_ADVISOR_TOOL_CALLED: 'ai_advisor_tool_called',
    AI_ADVISOR_TOOL_CONFIRMATION_REQUESTED: 'ai_advisor_tool_confirmation_requested',
    AI_ADVISOR_TOOL_CONFIRMATION_GRANTED: 'ai_advisor_tool_confirmation_granted',
    AI_ADVISOR_TOOL_CONFIRMATION_DENIED: 'ai_advisor_tool_confirmation_denied',
    AI_ADVISOR_PERSONA_SWITCHED: 'ai_advisor_persona_switched',
    // Sprint 48.0.0b — sub-agent orchestration. `_suggested` fires when
    // the persona-router surfaces a target persona; `_accepted` fires when
    // the user clicks through and a new chat thread is opened with the
    // handoff state seeded.
    AI_ADVISOR_HANDOFF_SUGGESTED: 'ai_advisor_handoff_suggested',
    AI_ADVISOR_HANDOFF_ACCEPTED: 'ai_advisor_handoff_accepted',
    // Sprint 48.0.0c — memory browse / curate. `_promoted` fires when an
    // admin locks a memory against retention GC; `_gc_swept` fires once
    // per nightly retention sweep with the count metadata.
    AI_ADVISOR_MEMORY_UPDATED: 'ai_advisor_memory_updated',
    AI_ADVISOR_MEMORY_DELETED: 'ai_advisor_memory_deleted',
    AI_ADVISOR_MEMORY_PROMOTED: 'ai_advisor_memory_promoted',
    AI_ADVISOR_MEMORY_GC_SWEPT: 'ai_advisor_memory_gc_swept',
    AI_ADVISOR_PROVIDER_SWITCHED: 'ai_advisor_provider_switched',
    AI_ADVISOR_RATE_LIMIT_EXCEEDED: 'ai_advisor_rate_limit_exceeded',
    AI_ADVISOR_PROMPT_INJECTION_BLOCKED: 'ai_advisor_prompt_injection_blocked',
    AI_ADVISOR_TASK_UPDATED: 'ai_advisor_task_updated',
    AI_ADVISOR_SEARCH_REGULATORY: 'ai_advisor_search_regulatory',
    AI_ADVISOR_CITATION_UNVERIFIED: 'ai_advisor_citation_unverified',
    AI_ADVISOR_REGULATORY_PACKS_INDEXED: 'ai_advisor_regulatory_packs_indexed',
    AI_ADVISOR_REGULATORY_PACK_ADDED: 'ai_advisor_regulatory_pack_added',
    // AI Advisor cross-module loop (47.5.8)
    AI_ADVISOR_EVIDENCE_STATUS_CHANGED: 'ai_advisor_evidence_status_changed',
    AI_ADVISOR_LINEAGE_VIEWED: 'ai_advisor_lineage_viewed',
    AI_ADVISOR_LINEAGE_EXPORTED: 'ai_advisor_lineage_exported',
    AI_ADVISOR_TASK_CONVERTED_TO_WORKFLOW_RUN: 'ai_advisor_task_converted_to_workflow_run',
    AI_ADVISOR_RECORD_SUMMARY_FETCHED: 'ai_advisor_record_summary_fetched',
    // Cross-module AI workflow exemplar (47.9.6) — pattern adopted by all
    // 48.1.x + 48.2.x cross-module workflows. Three events per workflow run:
    // start → produced → accepted/rejected/partially_accepted. Per
    // docs/engineering/forseti-cross-module-workflows.md §3 "audit trail".
    AI_ADVISOR_WORKFLOW_STARTED: 'ai_advisor_workflow_started',
    AI_ADVISOR_ASSISTED_DRAFT_PRODUCED: 'ai_advisor_assisted_draft_produced',
    AI_ADVISOR_DRAFT_ACCEPTED: 'ai_advisor_draft_accepted',
    AI_ADVISOR_DRAFT_REJECTED: 'ai_advisor_draft_rejected',
    AI_ADVISOR_DRAFT_PARTIALLY_ACCEPTED: 'ai_advisor_draft_partially_accepted',
    // AI Workflows (47.5.5)
    AI_WORKFLOW_CREATED: 'ai_workflow_created',
    AI_WORKFLOW_UPDATED: 'ai_workflow_updated',
    AI_WORKFLOW_DELETED: 'ai_workflow_deleted',
    AI_WORKFLOW_SHARED: 'ai_workflow_shared',
    AI_WORKFLOW_HIDDEN: 'ai_workflow_hidden',
    AI_WORKFLOW_RUN_STARTED: 'ai_workflow_run_started',
    AI_WORKFLOW_RUN_COMPLETED: 'ai_workflow_run_completed',
    AI_WORKFLOW_RUN_FAILED: 'ai_workflow_run_failed',
    AI_WORKFLOW_RUN_CANCELLED: 'ai_workflow_run_cancelled',
    AI_WORKFLOW_GENERATED_ATTACHMENT: 'ai_workflow_generated_attachment',
    AI_WORKFLOW_GENERATED_TASK: 'ai_workflow_generated_task',
    AI_WORKFLOW_RELOAD_BUILTIN: 'ai_workflow_reload_builtin',
    // AI Tabular Reviews (47.5.6)
    AI_TABULAR_REVIEW_CREATED: 'ai_tabular_review_created',
    AI_TABULAR_REVIEW_COLUMN_ADDED: 'ai_tabular_review_column_added',
    AI_TABULAR_REVIEW_COLUMN_REMOVED: 'ai_tabular_review_column_removed',
    AI_TABULAR_REVIEW_CELL_EXTRACTED: 'ai_tabular_review_cell_extracted',
    AI_TABULAR_REVIEW_CELL_FAILED: 'ai_tabular_review_cell_failed',
    AI_TABULAR_REVIEW_RUN_STARTED: 'ai_tabular_review_run_started',
    AI_TABULAR_REVIEW_RUN_COMPLETED: 'ai_tabular_review_run_completed',
    AI_TABULAR_REVIEW_RUN_CANCELLED: 'ai_tabular_review_run_cancelled',
    AI_TABULAR_REVIEW_APPLIED_WORKFLOW: 'ai_tabular_review_applied_workflow',
    // AI Document Review / Redlines (47.5.3)
    AI_DOCUMENT_EDIT_PROPOSED: 'ai_document_edit_proposed',
    AI_DOCUMENT_EDIT_ACCEPTED: 'ai_document_edit_accepted',
    AI_DOCUMENT_EDIT_REJECTED: 'ai_document_edit_rejected',
    AI_DOCUMENT_VERSION_PROMOTED: 'ai_document_version_promoted',
    // Provider + token billing (47.5.1)
    AI_PROVIDER_API_KEY_SET: 'ai_provider_api_key_set',
    AI_PROVIDER_API_KEY_ROTATED: 'ai_provider_api_key_rotated',
    AI_PROVIDER_API_KEY_CLEARED: 'ai_provider_api_key_cleared',
    AI_PROVIDER_HEALTH_CHECKED: 'ai_provider_health_checked',
    AI_PROVIDER_MODELS_DISCOVERED: 'ai_provider_models_discovered',
    AI_USAGE_LEDGER_WRITTEN: 'ai_usage_ledger_written',
    AI_QUOTA_EXHAUSTED: 'ai_quota_exhausted',
    AI_QUOTA_OVERRIDE_GRANTED: 'ai_quota_override_granted',
    AI_QUOTA_TOPPED_UP: 'ai_quota_topped_up',
    AI_QUOTA_RECONCILIATION_DRIFT_ALERT: 'ai_quota_reconciliation_drift_alert',
    // Self-service "Buy More" billing (48.5.x)
    AI_BILLING_INTENT_CREATED: 'ai_billing_intent_created',
    AI_BILLING_UNITS_PURCHASED: 'ai_billing_units_purchased',
    // AI Projects (47.5.2)
    AI_PROJECT_CREATED: 'ai_project_created',
    AI_PROJECT_RENAMED: 'ai_project_renamed',
    AI_PROJECT_DELETED: 'ai_project_deleted',
    AI_PROJECT_MEMBER_ADDED: 'ai_project_member_added',
    AI_PROJECT_MEMBER_REMOVED: 'ai_project_member_removed',
    AI_PROJECT_MEMBER_ROLE_CHANGED: 'ai_project_member_role_changed',
    AI_PROJECT_FOLDER_CREATED: 'ai_project_folder_created',
    AI_PROJECT_FOLDER_RENAMED: 'ai_project_folder_renamed',
    AI_PROJECT_FOLDER_DELETED: 'ai_project_folder_deleted',
    AI_PROJECT_DOCUMENT_ATTACHED: 'ai_project_document_attached',
    AI_PROJECT_DOCUMENT_UNATTACHED: 'ai_project_document_unattached',
    // AI evidence status transitions (per family plan §17.2 — 47.5.5 / 47.5.8)
    EVIDENCE_DRAFT_CREATED_BY_AI: 'evidence_draft_created_by_ai',
    EVIDENCE_MOVED_TO_REVIEW: 'evidence_moved_to_review',
    EVIDENCE_APPROVED: 'evidence_approved',
    EVIDENCE_REJECTED: 'evidence_rejected',
    // Sprint 58.0.0 (Phase 25 / Sprint A) — PDPL Evidence Pack assembly
    // lifecycle. Emitted by PdplEvidencePackZipService. Each event carries
    // pack_id + pack_sha256 + pack_type + dossier_id + org_slug + an
    // assembled_via flag ('deterministic' or 'ai_assisted', the latter
    // when Sprint G's Forseti workflow supplied ai_assistance). Send-side
    // lifecycle events (manual proof / direct send / authority response)
    // land in Sprints C and D.
    PDPL_EVIDENCE_PACK_ASSEMBLY_STARTED: 'pdpl_evidence_pack_assembly_started',
    PDPL_EVIDENCE_PACK_FILENAME_VALIDATION_PASSED: 'pdpl_evidence_pack_filename_validation_passed',
    PDPL_EVIDENCE_PACK_FILENAME_VALIDATION_FAILED: 'pdpl_evidence_pack_filename_validation_failed',
    PDPL_EVIDENCE_PACK_MANIFEST_GENERATED: 'pdpl_evidence_pack_manifest_generated',
    PDPL_EVIDENCE_PACK_ZIP_CREATED: 'pdpl_evidence_pack_zip_created',
    PDPL_EVIDENCE_PACK_ASSEMBLY_FAILED: 'pdpl_evidence_pack_assembly_failed',
    // Sprint 58.1.0 (Phase 25 / Sprint B) — PDPL email-draft + manual
    // sent-proof lifecycle. `draft_finalized` is reserved for Sprint D's
    // direct-send wiring so D doesn't have to touch the shared audit
    // taxonomy again. Each event carries pack_id + draft_id + version +
    // locale + pack_sha256. `sent_proof_uploaded` additionally carries
    // proof_id + envelope.attachment_sha256_at_send so chain-of-custody
    // queries can match the proof to the assembled pack.
    PDPL_EMAIL_DRAFT_CREATED: 'pdpl_email_draft_created',
    PDPL_EMAIL_DRAFT_EDITED: 'pdpl_email_draft_edited',
    PDPL_EMAIL_DRAFT_FINALIZED: 'pdpl_email_draft_finalized',
    PDPL_EMAIL_SENT_PROOF_UPLOADED: 'pdpl_email_sent_proof_uploaded',
    // Sprint 58.2.0 (Phase 25 / Sprint C) — PDPL email-provider config
    // lifecycle. Per-tenant customer-controlled outbound mail config.
    // Architecture lock: AesirX MUST NOT be the default sender — these
    // events record the customer's own provider standing up + consent
    // chain-of-custody (verbatim disclaimer text sha256 in the consent
    // event payload). Connection-check events carry latency_ms +
    // sanitised failure_reason; the actual configured password is never
    // emitted into the audit feed.
    PDPL_EMAIL_PROVIDER_CREATED: 'pdpl_email_provider_created',
    PDPL_EMAIL_PROVIDER_CONSENT_ACKNOWLEDGED: 'pdpl_email_provider_consent_acknowledged',
    PDPL_EMAIL_PROVIDER_CONNECTION_CHECK_SUCCEEDED: 'pdpl_email_provider_connection_check_succeeded',
    PDPL_EMAIL_PROVIDER_CONNECTION_CHECK_FAILED: 'pdpl_email_provider_connection_check_failed',
    PDPL_EMAIL_PROVIDER_DISABLED: 'pdpl_email_provider_disabled',
    // Sprint 58.3.0 (Phase 25 / Sprint D) — Direct-send lifecycle. Each
    // event carries org_id + send_id + pack_id + provider_config_id.
    // Send events additionally carry latency_ms + message_id (on success)
    // or sanitised failure_reason (on failure). Authority response events
    // carry classification + (on reclassification) previous + reason.
    // Hard-copy events carry courier + tracking_number.
    PDPL_EMAIL_SEND_ATTEMPTED: 'pdpl_email_send_attempted',
    PDPL_EMAIL_SEND_SUCCEEDED: 'pdpl_email_send_succeeded',
    PDPL_EMAIL_SEND_FAILED: 'pdpl_email_send_failed',
    PDPL_AUTHORITY_RESPONSE_RECORDED: 'pdpl_authority_response_recorded',
    PDPL_AUTHORITY_RESPONSE_RECLASSIFIED: 'pdpl_authority_response_reclassified',
    PDPL_HARD_COPY_FOLLOWUP_RECORDED: 'pdpl_hard_copy_followup_recorded',
    PDPL_HARD_COPY_FOLLOWUP_UPDATED: 'pdpl_hard_copy_followup_updated',
    // Sprint 62.2.1 — cross-framework evidence pack lifecycle. Framework-agnostic
    // counterpart to the PDPL events above; the PDPL family stays as it is,
    // because those events name a specific regulator-facing artefact and
    // renaming them would break shipped audit queries.
    //
    // `PREVIEW_RESOLVED` carries root_framework_code + node_count + item_count +
    // missing_count, so a preview that returned a short pack is reconstructable
    // after the underlying content changes. It is the only event 62.2.1 emits:
    // the assembly, approval and dispatch events belong to the sprints that
    // enforce those transitions, and declaring them here would put codes in the
    // taxonomy that nothing writes.
    FRAMEWORK_EVIDENCE_PACK_PREVIEW_RESOLVED: 'framework_evidence_pack_preview_resolved',
    /**
     * Sprint 62.2.3 security review, finding #2. Assembly and export emitted
     * **no audit event at all** — only a `log.info`. Minting an immutable,
     * hash-bearing artefact and writing a downloadable archive of an
     * organisation's entire regulatory posture to storage left nothing in the
     * tamper-evident chain. `created_by` and `exported_by` are module state, not
     * an audit record.
     *
     * Export is precisely the exfiltration surface `framework_evidence_pack:create`
     * was introduced to protect, and it was the one action with no accountability
     * record. The PDPL adapter this series folded onto the same core logs five
     * lifecycle events; the generic path logged none.
     */
    FRAMEWORK_EVIDENCE_PACK_ASSEMBLED: 'framework_evidence_pack_assembled',
    FRAMEWORK_EVIDENCE_PACK_EXPORTED: 'framework_evidence_pack_exported',
    /**
     * Sprint 62.2.4 — the transitions that actually reach a regulator.
     *
     * 62.2.3's security review found assembly and export emitting **no** audit
     * event at all. These three are the ones where the omission would matter
     * most: approval is the human judgement the parent sprint makes mandatory,
     * and the two dispatch events are the moment ComplianceOne communicates with
     * an authority. A filing with no record of who approved it, and no record
     * that it was sent, is a filing nobody can account for afterwards.
     *
     * A failed dispatch is audited too — `DISPATCH_FAILED` — because "we tried
     * and it bounced" is part of the compliance narrative, not an absence of one.
     */
    FRAMEWORK_EVIDENCE_PACK_APPROVED: 'framework_evidence_pack_approved',
    FRAMEWORK_EVIDENCE_PACK_DISPATCHED: 'framework_evidence_pack_dispatched',
    FRAMEWORK_EVIDENCE_PACK_DISPATCH_FAILED: 'framework_evidence_pack_dispatch_failed',
    /**
     * Sprint 64.33.0 — the bytes of a hard-copy dispatch proof, uploaded against
     * the pack through the pack-scoped route.
     *
     * Distinct from the attachment audit `GenericAttachmentService.upload` writes
     * for every file: that one records that a file entered the store, this one
     * records that a **regulator-facing proof of posting was produced for this
     * pack by this actor**, which is the fact a filing review asks about. The
     * dispatch record that references it is audited separately, and may never be
     * written — a proof uploaded and then not filed is itself worth being able to
     * see.
     */
    FRAMEWORK_EVIDENCE_PACK_PROOF_UPLOADED: 'framework_evidence_pack_proof_uploaded',
    /**
     * Sprint 62.3.0 — the AI register's lifecycle.
     *
     * AC4 requires the obligation surfaces to be "independently statused **and
     * audited**", and AC8 requires audit lineage. Every transition below changes
     * something a regulator could later ask about, so each has its own code
     * rather than one `ai_governance_changed` catch-all: "who classified this
     * system as high-risk, on which version of the list, and who approved it" has
     * to be answerable from the audit trail alone.
     *
     * `CLASSIFICATION_RUN_CREATED` records a *proposal*. It is deliberately a
     * different event from `CLASSIFICATION_REVIEWED`, which records the decision —
     * conflating them would make the platform's screening output indistinguishable
     * from a human classification in the one record built to tell them apart.
     */
    AI_SYSTEM_PROFILE_CREATED: 'ai_system_profile_created',
    AI_SYSTEM_PROFILE_UPDATED: 'ai_system_profile_updated',
    AI_CLASSIFICATION_RUN_CREATED: 'ai_classification_run_created',
    AI_CLASSIFICATION_REVIEWED: 'ai_classification_reviewed',
    AI_OBLIGATIONS_INSTANTIATED: 'ai_obligations_instantiated',
    AI_OBLIGATION_UPDATED: 'ai_obligation_updated',
    /** Sprint 62.48.0, wave 4 (AC14) — an AI risk-management framework approved, as a new version. */
    AI_RISK_FRAMEWORK_RECORDED: 'ai_risk_framework_recorded',
    /**
     * Sprint 62.48.0, wave 4 (AC14) — a three-lines map recorded: units and holders. Fix-sprint-20261031-1200 F6: the
     * map records no attestation; an attestation carried forward from an unchanged line is named in the details.
     */
    AI_THREE_LINES_RECORDED: 'ai_three_lines_recorded',
    /** Fix-sprint-20261031-1200 F6 — one line of the current map attested by one of its own holders: the line, the version, the signatory. */
    AI_THREE_LINE_ATTESTED: 'ai_three_line_attested',
    /**
     * Sprint 62.29.0 — a governance evidence entry recorded, and a reviewer's
     * confirmation of one. Two events for the same reason the classification has
     * two: recording evidence and confirming it are different acts.
     */
    AI_GOVERNANCE_EVIDENCE_RECORDED: 'ai_governance_evidence_recorded',
    AI_GOVERNANCE_EVIDENCE_REVIEWED: 'ai_governance_evidence_reviewed',
    /**
     * Sprint 62.48.0, wave 1A (AC6, AC8, AC9) — version history, change control and
     * rollback; the security-test record per attack category; the independent
     * validation sign-off and its findings. Each is its own event: recording a
     * version is not a change record, and a sign-off is not a finding's closure.
     */
    AI_SYSTEM_VERSION_RECORDED: 'ai_system_version_recorded',
    AI_VERSION_CHANGE_RECORDED: 'ai_version_change_recorded',
    /** Gate E12.5 CR-P2-3 — a significance answer, with its basis, recorded on a change that was recorded without one. */
    AI_VERSION_SIGNIFICANCE_RECORDED: 'ai_version_significance_recorded',
    AI_SYSTEM_ROLLBACK_RECORDED: 'ai_system_rollback_recorded',
    AI_ROLLBACK_PLAN_RECORDED: 'ai_rollback_plan_recorded',
    AI_SECURITY_TEST_RECORDED: 'ai_security_test_recorded',
    AI_SECURITY_TEST_UPDATED: 'ai_security_test_updated',
    AI_VALIDATION_OPENED: 'ai_validation_opened',
    AI_VALIDATION_SIGNED: 'ai_validation_signed',
    AI_VALIDATION_FINDING_RECORDED: 'ai_validation_finding_recorded',
    /** A signed validation superseded by a later version of its system. */
    AI_VALIDATION_STALE: 'ai_validation_stale',
    /**
     * Sprint 62.48.0, wave 1B — the annual inventory confirmation (AC2), the impact
     * assessment and its approval (AC5 core), and the release approval and the
     * stale marking it can receive (AC7). Each is its own event.
     */
    AI_INVENTORY_REVIEW_CONFIRMED: 'ai_inventory_review_confirmed',
    AI_IMPACT_ASSESSMENT_RECORDED: 'ai_impact_assessment_recorded',
    AI_IMPACT_ASSESSMENT_APPROVED: 'ai_impact_assessment_approved',
    AI_RELEASE_APPROVAL_RECORDED: 'ai_release_approval_recorded',
    AI_RELEASE_APPROVAL_STALE: 'ai_release_approval_stale',
    /**
     * Sprint 62.48.0, wave 2 — the payment authority, its events (AC3), the
     * interventions (AC4), the monitoring records (AC10), the duty holders and their
     * competence reviews (AC18), the capacity records and the serious-incident record
     * (AC17). Each is its own event.
     */
    AI_PAYMENT_AUTHORITY_RECORDED: 'ai_payment_authority_recorded',
    AI_PAYMENT_AUTHORITY_EVENT_RECORDED: 'ai_payment_authority_event_recorded',
    AI_INTERVENTION_RECORDED: 'ai_intervention_recorded',
    AI_INTERVENTION_CONFIRMED: 'ai_intervention_confirmed',
    AI_ESCALATION_RESPONDED: 'ai_escalation_responded',
    AI_CHANNEL_RECORDED: 'ai_channel_recorded',
    AI_STAFF_BRIEFING_RECORDED: 'ai_staff_briefing_recorded',
    AI_MONITORING_THRESHOLDS_SET: 'ai_monitoring_thresholds_set',
    AI_MONITORING_COMPARISON_RECORDED: 'ai_monitoring_comparison_recorded',
    AI_MONITORING_EVENT_RECORDED: 'ai_monitoring_event_recorded',
    AI_MONITORING_EVENT_CLOSED: 'ai_monitoring_event_closed',
    AI_DUTY_HOLDER_RECORDED: 'ai_duty_holder_recorded',
    AI_COMPETENCE_REVIEW_RECORDED: 'ai_competence_review_recorded',
    AI_COMPETENCE_CAPACITY_RECORDED: 'ai_competence_capacity_recorded',
    AI_INCIDENT_RECORD_UPDATED: 'ai_incident_record_updated',
    /**
     * Sprint 62.48.0, wave 3 — the impact reopening and fairness section (AC5), disclosures
     * (AC11), complaints, re-review, closure and the threshold (AC12), decisions (AC13),
     * supplier arrangements and version reports (AC15), the component register, integrity
     * records, advisories and supply-chain reviews (AC16), and data provenance (AC19).
     */
    AI_IMPACT_ASSESSMENT_REOPENED: 'ai_impact_assessment_reopened',
    AI_FAIRNESS_SECTION_RECORDED: 'ai_fairness_section_recorded',
    AI_DISCLOSURE_RECORDED: 'ai_disclosure_recorded',
    AI_COMPLAINT_RECORDED: 'ai_complaint_recorded',
    AI_COMPLAINT_ROUTED: 'ai_complaint_routed',
    AI_COMPLAINT_RE_REVIEWED: 'ai_complaint_re_reviewed',
    AI_COMPLAINT_CLOSED: 'ai_complaint_closed',
    AI_COMPLAINT_THRESHOLD_SET: 'ai_complaint_threshold_set',
    /** fix-sprint-20261031-1200 F8 — the organisation's response to a pattern of complaints, answering the threshold task. */
    AI_COMPLAINT_PATTERN_RESPONSE_RECORDED: 'ai_complaint_pattern_response_recorded',
    /** fix-sprint-20261031-1200 F37 — an explanation delivered after the re-review (UC-VN-62 step 5). */
    AI_COMPLAINT_EXPLANATION_RECORDED: 'ai_complaint_explanation_recorded',
    AI_DECISION_RECORDED: 'ai_decision_recorded',
    AI_SUPPLIER_ARRANGEMENT_RECORDED: 'ai_supplier_arrangement_recorded',
    AI_SUPPLIER_VERSION_REPORTED: 'ai_supplier_version_reported',
    /**
     * Fix-sprint-20261031-1200 F26: a model-version report failed part-way (a later system's write, or a termination
     * that landed first), and the systems already written were put back. The version-path rows it wrote stay in the
     * trail; this says they were undone.
     */
    AI_SUPPLIER_VERSION_REPORT_UNDONE: 'ai_supplier_version_report_undone',
    /** Walk cluster F (G28–G32): a sub-processor change, a termination, a regulator request, contact or log, a continuity test, a monitoring review or an arrangement review. */
    AI_SUPPLIER_EVENT_RECORDED: 'ai_supplier_event_recorded',
    AI_LEGACY_INVENTORY_IMPORTED: 'ai_legacy_inventory_imported',
    AI_COMPONENT_REGISTER_RECORDED: 'ai_component_register_recorded',
    AI_COMPONENT_INTEGRITY_RECORDED: 'ai_component_integrity_recorded',
    AI_COMPONENT_ADVISORY_RECORDED: 'ai_component_advisory_recorded',
    AI_SUPPLY_CHAIN_REVIEW_RECORDED: 'ai_supply_chain_review_recorded',
    AI_DATA_PROVENANCE_RECORDED: 'ai_data_provenance_recorded',
    /**
     * Sprint 62.3.0, gate E12.5 P2-f. Export had no audit event of its own, and
     * the assembly service audits under `case_id = pack_id` — so "who exported
     * regulator-facing evidence for AI system X" was unanswerable from the
     * profile's own trail, which is the trail an auditor reads.
     */
    AI_EVIDENCE_EXPORTED: 'ai_evidence_exported',
    /**
     * Sprint 64.38.0 — a screening moved to `superseded` by the pending-row dedup
     * migration, not by a reviewer.
     *
     * Deliberately **not** `AI_CLASSIFICATION_REVIEWED`. That event says a person
     * decided something, and reusing it here would put a decision nobody made onto
     * the one trail an auditor reads to find out who decided what. The actor is
     * `system:migration` and the payload names the migration, the sprint and the
     * run that stayed pending.
     */
    AI_CLASSIFICATION_PENDING_ROW_RESOLVED: 'ai_classification_pending_row_resolved',
    /**
     * Sprint 62.4.0 — regulatory classification.
     *
     * The same split as the AI events above, for the same reason:
     * `CLASSIFICATION_ASSESSMENT_CREATED` records a *proposal* the platform
     * computed, `CLASSIFICATION_ASSESSMENT_REVIEWED` records the *decision* a
     * person made. Conflating them would make the platform's screening output
     * indistinguishable from a human classification in the one record built to
     * tell them apart.
     *
     * `CLASSIFICATION_LEGACY_TIER_WRITTEN` is separate from the review event and
     * exists because approving an assessment can write the pre-existing
     * `data_tier` field on a canonical registry item — which until Sprint 62.4.0
     * was only ever set by an unsourced dropdown. (This said the field was read
     * by "211 call sites". Sprint 64.73.0 re-measured with the TypeScript
     * language service while retiring the `DataTier` union: the union had 14
     * type-level references in 6 files.) A value that starts driving obligations because of an approval
     * elsewhere in the product must be traceable to that approval from the
     * canonical record's own trail, not only from the classification module's.
     *
     * `CLASSIFICATION_LEGACY_TIER_CLEARED` is its counterpart, added by gate
     * E12.5's F1. The write was one-directional: approving a tier that declares a
     * `platform_data_tier` wrote it, and approving a tier that declares **none**
     * returned early — so a subject downgraded from a mapped tier to an unmapped
     * one kept the old legacy value on the canonical record. The profile said the
     * classification in force was the new one while the canonical record
     * still said the old one, with nothing on screen and no audit row. Clearing
     * the stale value is a change to the same field for the same reason, so it is
     * audited under the same canonical record.
     */
    CLASSIFICATION_SUBJECT_PROFILE_CREATED: 'classification_subject_profile_created',
    CLASSIFICATION_SUBJECT_PROFILE_UPDATED: 'classification_subject_profile_updated',
    CLASSIFICATION_ASSESSMENT_CREATED: 'classification_assessment_created',
    CLASSIFICATION_ASSESSMENT_REVIEWED: 'classification_assessment_reviewed',
    CLASSIFICATION_LEGACY_TIER_WRITTEN: 'classification_legacy_tier_written',
    CLASSIFICATION_LEGACY_TIER_CLEARED: 'classification_legacy_tier_cleared',
    /**
     * Sprint 64.38.0 — the assessment twin of
     * `AI_CLASSIFICATION_PENDING_ROW_RESOLVED`, for the same reason and with
     * the same actor. See that entry.
     */
    CLASSIFICATION_PENDING_ROW_RESOLVED: 'classification_pending_row_resolved',
    PROTECTIVE_MEASURES_INSTANTIATED: 'protective_measures_instantiated',
    PROTECTIVE_MEASURE_UPDATED: 'protective_measure_updated',
    /**
     * Sprint 65.7.0 (AC3) — a control was exercised and the outcome recorded.
     *
     * Separate from `PROTECTIVE_MEASURE_UPDATED` because it is a different act.
     * Updating a measure records what somebody typed about its status; a test
     * records that the control was examined, by whom, over what, and what the
     * examination concluded. Folding the two together would make "when was this
     * control last actually tested?" unanswerable from the trail — which is the
     * question the record exists for.
     */
    PROTECTIVE_MEASURE_TESTED: 'protective_measure_tested',
    REGULATED_OFFERING_CREATED: 'regulated_offering_created',
    REGULATED_OFFERING_UPDATED: 'regulated_offering_updated',
    CLASSIFICATION_EVIDENCE_EXPORTED: 'classification_evidence_exported',
    /**
     * Sprint 62.20.0 — a scheme entry added to or removed from a subject profile.
     *
     * Separate from `CLASSIFICATION_SUBJECT_PROFILE_UPDATED` because it is a
     * different act: adding an entry declares that this subject is now classified
     * under a second instrument's ladder, which is the fact an auditor asking
     * "which schemes does this asset answer to, and since when" needs the trail
     * for. It carries NO tier — a tier in force is only ever written by a review.
     */
    CLASSIFICATION_SCHEME_ENTRY_CHANGED: 'classification_scheme_entry_changed',
    /**
     * Sprint 62.20.0 — a human's stated correspondence between a subject's
     * classifications under different schemes.
     *
     * Its own event because nothing computes it and nothing validates one leg
     * against another. The audit row is the only place the platform can say who
     * asserted the correspondence and when — which is the entire evidentiary
     * value of a record that the platform will never derive.
     */
    CLASSIFICATION_CROSS_MAPPING_RECORDED: 'classification_cross_mapping_recorded',
    /**
     * Sprint 62.20.0 — a reviewer's decision on a pack-declared upward
     * reclassification prompt.
     *
     * Distinct from `CLASSIFICATION_ASSESSMENT_REVIEWED`: that decides a
     * screening the platform proposed, this answers a statement the PACK made
     * about aggregation. The platform proposes nothing here and reclassifies
     * nothing on its own; without a row of this type, no tier moved.
     */
    CLASSIFICATION_AGGREGATION_REVIEWED: 'classification_aggregation_reviewed',
    /**
     * Sprint 62.26.0 (AC9) — a person retired a transitional (predecessor)
     * classification entry after a pack-declared window. The platform never
     * retires one; without a row of this type, nothing was retired.
     */
    CLASSIFICATION_TRANSITIONAL_RETIREMENT_RECORDED: 'classification_transitional_retirement_recorded',
    /**
     * Sprint 62.20.0 AC5 — the regulatory placeholder register.
     *
     * Three events for a register that triggers nothing, because "nothing was
     * triggered" is only checkable if the intake itself is traceable. Recording
     * an emergency order that restricts nothing still has to be evidence that the
     * order was received.
     */
    REGULATORY_PLACEHOLDER_RECORDED: 'regulatory_placeholder_recorded',
    REGULATORY_PLACEHOLDER_UPDATED: 'regulatory_placeholder_updated',
    /**
     * Sprint 62.5.0 — trust evidence: identity, trust services, and electronic
     * transactions.
     *
     * ## Why these exist at all (gate E12.5 F1)
     *
     * The module shipped auditing **exclusively** through
     * `maybeIngestToGroupAudit`, which returns early for an organisation with no
     * `group_id` (`audit-hook.ts:33`). A single-organisation tenant — the most
     * common shape — could revoke a certificate and amend a filing to an
     * authority and neither `grc_case_audit_events` nor
     * `grc_group_audit_consolidated` would receive anything. For a compliance
     * platform that is the product failing quietly.
     *
     * The module's own rationale for not reaching `AuditService` was that doing so
     * needs "an edit to shared constants or an `as never` cast", treating the
     * supported path as equivalent to 62.2.1's cast defect. It is not: every peer
     * — evidence packs, AI governance, regulatory classification — **added
     * codes**, which is what these are.
     *
     * ## Naming
     *
     * `<ENTITY>_<PAST_TENSE_VERB>`, matching `FRAMEWORK_EVIDENCE_PACK_*` and
     * `AI_*` above, and the values are snake_case throughout. The module's
     * identity half previously emitted dotted values (`identity_profile.created`)
     * which no other event in this file uses; they are normalised here, because a
     * feed that filters on `event_type` should not need to know which sprint wrote
     * a row.
     *
     * ## One code per condition
     *
     * `TRUST_CREDENTIAL_TRANSITIONED` is separate from `TRUST_CREDENTIAL_UPDATED`
     * for the reason the AI and classification blocks above give: a lifecycle move
     * is the act a relying party depends on and it carries its own permission
     * (`trust_evidence:lifecycle`), while an update corrects a contact detail.
     * Collapsing them would make "who revoked this certificate, and when" a
     * question answerable only by reading payloads.
     *
     * `TRUST_CREDENTIAL_RENEWED` is likewise distinct from `_CREATED`: a renewal
     * writes a pointer on a *second* record, so "why did this credential stop
     * being the current one" has to be answerable from the trail rather than
     * inferred from two timestamps.
     */
    IDENTITY_PROFILE_CREATED: 'identity_profile_created',
    IDENTITY_PROFILE_UPDATED: 'identity_profile_updated',
    IDENTITY_ATTRIBUTE_CREATED: 'identity_attribute_created',
    IDENTITY_ATTRIBUTE_UPDATED: 'identity_attribute_updated',
    IDENTITY_EVENT_RECORDED: 'identity_event_recorded',
    TRUST_PROVIDER_CREATED: 'trust_service_provider_created',
    TRUST_PROVIDER_UPDATED: 'trust_service_provider_updated',
    TRUST_CREDENTIAL_CREATED: 'trust_credential_created',
    TRUST_CREDENTIAL_UPDATED: 'trust_credential_updated',
    TRUST_CREDENTIAL_TRANSITIONED: 'trust_credential_transitioned',
    TRUST_CREDENTIAL_RENEWED: 'trust_credential_renewed',
    TRUST_VERIFICATION_RECORDED: 'trust_verification_recorded',
    ELECTRONIC_SUBMISSION_CREATED: 'electronic_submission_created',
    ELECTRONIC_SUBMISSION_UPDATED: 'electronic_submission_updated',
    ELECTRONIC_SUBMISSION_SUBMITTED: 'electronic_submission_submitted',
    ELECTRONIC_SUBMISSION_RECEIPT_RECORDED: 'electronic_submission_receipt_recorded',
    ELECTRONIC_SUBMISSION_AMENDED: 'electronic_submission_amended',
    ELECTRONIC_AGREEMENT_CREATED: 'electronic_agreement_created',
    ELECTRONIC_AGREEMENT_UPDATED: 'electronic_agreement_updated',
    ELECTRONIC_AGREEMENT_AMENDED: 'electronic_agreement_amended',
    ELECTRONIC_AGREEMENT_SYNC_RECORDED: 'electronic_agreement_sync_recorded',
    ELECTRONIC_AGREEMENT_EXCEPTION_RAISED: 'electronic_agreement_exception_raised',
    ELECTRONIC_AGREEMENT_EXCEPTION_RESOLVED: 'electronic_agreement_exception_resolved',
    /**
     * The counterpart of `AI_EVIDENCE_EXPORTED` and
     * `CLASSIFICATION_EVIDENCE_EXPORTED`, and added for the reason 62.3.0's gate
     * E12.5 P2-f gave: the assembler audits under its own `pack_id`, so "who
     * exported regulator-facing evidence for credential X" is unanswerable from
     * the credential's own trail — which is the trail an auditor reads.
     */
    TRUST_EVIDENCE_EXPORTED: 'trust_evidence_exported',
    /**
     * Sprint 62.6.0 — the regulatory artifact registry.
     *
     * These exist because the module emitted all six of them from day one while
     * this union had none, so `artifact-audit.ts` reached the platform trail
     * through an `as unknown as AuditActionType` cast. That is exactly the shape
     * Sprint 62.2.1 found and named: an audit call written against a contract
     * `AuditService` has never accepted, hidden by a cast, which type-checks
     * forever and is discovered only when someone reads the trail and finds
     * nothing they can filter on.
     *
     * The pair `CLASSIFIED` / `RECLASSIFIED` is kept apart deliberately. The first
     * time an artifact is classified it is usually the migration deciding from
     * evidence the record already carried; every time after, it is a person
     * overriding that. "Who changed what this was" and "what did the platform
     * infer at install" are different questions, and one event type cannot answer
     * both.
     *
     * `ACCESS_DENIED` is a first-class code rather than a failed `ACCESS_GRANTED`
     * because the question an inspection asks is not who read a restricted
     * register — it is who **tried**.
     */
    REGULATORY_ARTIFACT_CLASSIFIED: 'regulatory_artifact_classified',
    REGULATORY_ARTIFACT_RECLASSIFIED: 'regulatory_artifact_reclassified',
    REGULATORY_ARTIFACT_ACCESS_REQUESTED: 'regulatory_artifact_access_requested',
    REGULATORY_ARTIFACT_ACCESS_DENIED: 'regulatory_artifact_access_denied',
    /**
     * An attempt that **succeeded** — a non-`open` artifact was viewed,
     * downloaded or exported. `new_value.attempted` names which.
     *
     * Added by gate E12.5 P1-3, and the finding is worth stating because the
     * module shipped with the opposite half only. `grc_artifact_access_events`
     * has exactly one reader, `listEvents`, gated on
     * `regulatory_artifact:custody` — a permission the auditor role is
     * deliberately denied, because an auditor who can grant themselves access to
     * the records they audit is not an auditor. Denials were mirrored here and
     * successes were not. So an auditor holding `audit:events:read` could see
     * every person **refused** a restricted register and no person who **read**
     * one, which is the inversion of what an access review needs: a refusal is a
     * non-event, and a successful read of a restricted register is the event.
     *
     * The same argument `TRUST_EVIDENCE_EXPORTED` above was added on, and the
     * same remedy: the fact belongs on the trail an auditor already reads rather
     * than behind a second permission they are not meant to hold. It is the
     * narrower of the two available fixes — this row names one artifact and one
     * act, where opening `listEvents` to the auditor would hand over the whole
     * unfiltered map of what the organisation restricts.
     *
     * A read of an `open` artifact never reaches here. That exclusion is the
     * original volume argument and it still holds: every list render would
     * otherwise write a row and the trail would drown the signal it carries.
     */
    REGULATORY_ARTIFACT_ACCESSED: 'regulatory_artifact_accessed',
    REGULATORY_ARTIFACT_ACCESS_GRANTED: 'regulatory_artifact_access_granted',
    REGULATORY_ARTIFACT_ACCESS_REVOKED: 'regulatory_artifact_access_revoked',
    // ── Sprint 62.7.0 — public data exchange ─────────────────────
    //
    // The register's whole value to an inspection is that a transfer can be
    // proved, so the acts that make a transfer provable each get their own code
    // rather than one `updated`: recording the share, receiving the
    // acknowledgement, and the recipient refusing it are three different facts.
    PUBLIC_DATASET_CREATED: 'public_dataset_created',
    PUBLIC_DATASET_UPDATED: 'public_dataset_updated',
    PUBLIC_DATASET_DICTIONARY_VERSIONED: 'public_dataset_dictionary_versioned',
    PUBLIC_DATASET_DICTIONARY_CONFIRMED: 'public_dataset_dictionary_confirmed',
    PUBLIC_DATA_ARRANGEMENT_RECORDED: 'public_data_arrangement_recorded',
    /**
     * The descriptive half of an arrangement was amended.
     *
     * Its own code rather than a second use of `..._RECORDED`. The two answer
     * different questions — "when did we agree to share this?" and "what changed
     * about that agreement, and when?" — and an amendment that re-points the
     * arrangement at a different dictionary version changes what every subsequent
     * transfer is recorded against. A filter built from this list could not
     * separate the two while they shared a code.
     */
    PUBLIC_DATA_ARRANGEMENT_UPDATED: 'public_data_arrangement_updated',
    /**
     * Legal approved the basis and scope.
     *
     * Restored: an edit adding the four codes 62.7.0 authorised dropped this one,
     * which `public-data-service.ts:224` still emits, so the build broke on a code
     * the trail had silently lost. A share is recorded against an arrangement, and
     * UC-VN-35's approvals table makes Legal's approval of the basis a named act —
     * without its own code, "we agreed to share" and "Legal approved sharing"
     * collapse into one entry.
     */
    PUBLIC_DATA_ARRANGEMENT_APPROVED: 'public_data_arrangement_approved',
    PUBLIC_DATA_ARRANGEMENT_REVIEWED: 'public_data_arrangement_reviewed',
    PUBLIC_DATA_SHARE_RECORDED: 'public_data_share_recorded',
    PUBLIC_DATA_SHARE_ACKNOWLEDGED: 'public_data_share_acknowledged',
    PUBLIC_DATA_SHARE_REFUSED: 'public_data_share_refused',
    /**
     * A personal-data test was opened against a dataset.
     *
     * The row an inspection asks for that the other two cannot produce: **who
     * assessed this dataset and decided not to proceed.** A readiness that is
     * authored and never concluded, or concluded and never signed off, is a
     * deliberate stop — and without this code it left no trace anywhere, because
     * the only readiness row in the trail was written by the sign-off that never
     * happened.
     */
    OPEN_DATA_READINESS_RECORDED: 'open_data_readiness_recorded',
    /**
     * The test was answered: cleared, or blocked.
     *
     * Separate from the sign-off because they are separate acts by (potentially)
     * separate people, and the release gate reads both independently. Folding
     * them together would make one of the two refusals unreachable and would lose
     * the interval between "we decided" and "somebody stood behind it".
     */
    OPEN_DATA_READINESS_CONCLUDED: 'open_data_readiness_concluded',
    /**
     * The personal-data test was answered and signed off.
     *
     * Separate from the release itself because UC-VN-35 makes the sign-off the
     * blocking gate and the release its consequence. An inspection asks who
     * decided the dataset contained no personal data, and that question has a
     * different answer from who pressed publish.
     */
    OPEN_DATA_READINESS_SIGNED_OFF: 'open_data_readiness_signed_off',
    OPEN_DATA_RELEASED: 'open_data_released',
    OPEN_DATA_TAKEN_DOWN: 'open_data_taken_down',
    PUBLIC_DATA_QUALITY_ISSUE_RAISED: 'public_data_quality_issue_raised',
    /**
     * Somebody declined to act on a reported defect.
     *
     * A dismissal is a decision, not an absence, and the reported defect may have
     * come from outside the organisation. It carries a reason for the same reason
     * a refused transfer does: "dismissed, no reason recorded" is a record that
     * cannot be defended to the person who reported it.
     *
     * There is deliberately **no** `..._CORRECTED` code beside this one. An issue
     * becomes `corrected` when a `PublicDataCorrection` naming it is recorded, and
     * that act already writes `PUBLIC_DATA_CORRECTION_RECORDED`. A second code
     * would be a second record of one act, and the two could disagree.
     */
    PUBLIC_DATA_QUALITY_ISSUE_DISMISSED: 'public_data_quality_issue_dismissed',
    /**
     * A correction was recorded, and with it the derived recipient list.
     *
     * `new_value.recipient_snapshot_at` and the recipient count are the point:
     * the list is computed from the receipts that existed at that moment, and
     * without the moment the list is a claim with no time attached.
     */
    PUBLIC_DATA_CORRECTION_RECORDED: 'public_data_correction_recorded',
    PUBLIC_DATA_CORRECTION_NOTIFIED: 'public_data_correction_notified',
    // ── Sprint 62.7.0 — payments evidence ────────────────────────
    PAYMENT_SERVICE_REGISTERED: 'payment_service_registered',
    PAYMENT_SERVICE_UPDATED: 'payment_service_updated',
    PAYMENT_LOG_MAPPING_RECORDED: 'payment_log_mapping_recorded',
    /** Every attempt, including the failures. See `EkycVerification`. */
    EKYC_VERIFICATION_RECORDED: 'ekyc_verification_recorded',
    EKYC_VERIFICATION_REVIEWED: 'ekyc_verification_reviewed',
    EKYC_RETENTION_EXECUTED: 'ekyc_retention_executed',
    EKYC_RETENTION_EXTENDED: 'ekyc_retention_extended',
    /**
     * Someone with `payments_evidence:unmask` read a restricted handle.
     *
     * A first-class code, and the reason is 62.6.0's: the question an inspection
     * asks about protected material is who **saw** it. Since `:unmask` is held by
     * no system role, every row here is by construction the result of a
     * deliberate role assignment somebody made and can be asked about.
     */
    PROTECTED_REFERENCE_UNMASKED: 'protected_reference_unmasked',
    PAYMENT_DATA_SECURITY_RECORDED: 'payment_data_security_recorded',
    AML_DILIGENCE_LINKED: 'aml_diligence_linked',
    AML_DILIGENCE_CONFIRMED: 'aml_diligence_confirmed',
    RETENTION_RECONCILIATION_DRAFTED: 'retention_reconciliation_drafted',
    /**
     * One function signed. `new_value.function` names which, and the status the
     * signature produced — a first signature leaves the record awaiting the
     * second, and the trail must show that a single signature never completed it.
     */
    RETENTION_RECONCILIATION_APPROVED: 'retention_reconciliation_approved',
    PAYMENT_EXCEPTION_RAISED: 'payment_exception_raised',
    /**
     * Somebody took ownership of the decision.
     *
     * The transition that makes `under_review` reachable. An exception is a
     * conflict between two duties the organisation owes, and a conflict nobody
     * owns is a conflict nobody decides — so the act of naming the owner is a
     * first-class event rather than a field edit.
     */
    PAYMENT_EXCEPTION_ASSIGNED: 'payment_exception_assigned',
    PAYMENT_EXCEPTION_RESOLVED: 'payment_exception_resolved',
    /**
     * A completed reconciliation was replaced by a new one for the same data
     * element.
     *
     * `previous_value` carries the superseded record's own status and both
     * signatures: the old position was validly signed by two functions and stays
     * readable as history, which is why the collection's unique index is partial
     * on the non-superseded states.
     */
    RETENTION_RECONCILIATION_SUPERSEDED: 'retention_reconciliation_superseded',
    // ── Sprint 62.7.0 — platform evidence ────────────────────────
    PLATFORM_SELLER_REGISTERED: 'platform_seller_registered',
    PLATFORM_SELLER_VERIFIED: 'platform_seller_verified',
    PLATFORM_SELLER_REJECTED: 'platform_seller_rejected',
    /**
     * Reversible by design. A suspension that cannot be lifted is a deletion
     * under another name, and the register's purpose is history — so the trail
     * carries both the suspension and the verification that lifted it.
     */
    PLATFORM_SELLER_SUSPENDED: 'platform_seller_suspended',
    /** Terminal. Nothing moves a withdrawn registration anywhere. */
    PLATFORM_SELLER_WITHDRAWN: 'platform_seller_withdrawn',
    PLATFORM_TRANSACTION_RECORDED: 'platform_transaction_recorded',
    PLATFORM_DOCUMENT_RECORDED: 'platform_document_recorded',
    PLATFORM_DOCUMENT_CANCELLED: 'platform_document_cancelled',
    PLATFORM_RECONCILIATION_RUN: 'platform_reconciliation_run',
    PLATFORM_RECONCILIATION_EXCEPTION_RAISED: 'platform_reconciliation_exception_raised',
    PLATFORM_RECONCILIATION_EXCEPTION_RESOLVED: 'platform_reconciliation_exception_resolved',
    AUTHORITY_CORRESPONDENCE_OPENED: 'authority_correspondence_opened',
    AUTHORITY_CORRESPONDENCE_EVIDENCE_ASSEMBLED: 'authority_correspondence_evidence_assembled',
    AUTHORITY_CORRESPONDENCE_RESPONDED: 'authority_correspondence_responded',
    /**
     * The matter is concluded, which is not the same as having answered it.
     *
     * An authority can come back on the same request, so "we have answered and are
     * waiting" and "this is finished" are different positions — and a register
     * that collapsed them would leave an operator unable to see which of their
     * open matters still need watching.
     */
    AUTHORITY_CORRESPONDENCE_CLOSED: 'authority_correspondence_closed',
    // ── Sprint 62.8.0 — manufacturing operations ────────────────────
    //
    // Thirty codes across five sub-domains. Every mutating service method in the
    // manufacturing module writes exactly one of them, and the module's own suite
    // asserts that mapping in both directions — a code no method emits is dead
    // vocabulary that reads as coverage, and a method emitting no code is a change
    // nobody can see.
    //
    // The naming distinguishes **what the organisation did** from what changed in a
    // row, because the trail is read by somebody reconstructing a decision. A
    // conformity record moving to `declared_conforming` is `..._DECLARED`, not
    // `..._UPDATED`: the first says a legal statement was made and by whom, the
    // second says a column moved.
    MANUFACTURING_PRODUCT_REGISTERED: 'manufacturing_product_registered',
    MANUFACTURING_PRODUCT_UPDATED: 'manufacturing_product_updated',
    MANUFACTURING_BATCH_REGISTERED: 'manufacturing_batch_registered',
    MANUFACTURING_BATCH_UPDATED: 'manufacturing_batch_updated',
    /**
     * The trail has two trace codes and **no update or delete code**, because the
     * ledger has no update or delete path.
     *
     * `..._CORRECTED` is written against the **corrected** event, so reading one
     * event's trail answers "was this ever superseded, and when" without walking
     * the chain. The correcting event carries `..._APPENDED` like any other, which
     * is what it is.
     */
    MANUFACTURING_TRACE_EVENT_APPENDED: 'manufacturing_trace_event_appended',
    MANUFACTURING_TRACE_EVENT_CORRECTED: 'manufacturing_trace_event_corrected',
    MANUFACTURING_CONFORMITY_RECORDED: 'manufacturing_conformity_recorded',
    MANUFACTURING_CONFORMITY_UPDATED: 'manufacturing_conformity_updated',
    MANUFACTURING_CONFORMITY_EVIDENCE_ADDED: 'manufacturing_conformity_evidence_added',
    MANUFACTURING_CONFORMITY_FINDING_RAISED: 'manufacturing_conformity_finding_raised',
    MANUFACTURING_CONFORMITY_FINDING_RESOLVED: 'manufacturing_conformity_finding_resolved',
    /**
     * The legal act, and the reason it is not `..._UPDATED`.
     *
     * A declaration of conformity is a statement by the organisation placing goods
     * on a market. When a market-surveillance authority asks who declared and on
     * what date, the answer must be a distinct event in the trail rather than a
     * state transition somebody has to infer from a diff.
     */
    MANUFACTURING_CONFORMITY_DECLARED: 'manufacturing_conformity_declared',
    /** An assessment becomes historical. Reversible by nothing; the record stays readable. */
    MANUFACTURING_CONFORMITY_SUPERSEDED: 'manufacturing_conformity_superseded',
    MANUFACTURING_CAPA_OPENED: 'manufacturing_capa_opened',
    MANUFACTURING_CAPA_UPDATED: 'manufacturing_capa_updated',
    /**
     * A corrective action becomes a recall.
     *
     * Its own code because the escalation is the moment the organisation's exposure
     * changes — from an internal fix to an act a distributor or the public sees.
     */
    MANUFACTURING_CAPA_ESCALATED_TO_RECALL: 'manufacturing_capa_escalated_to_recall',
    /**
     * Closure approved, by somebody who is **not** the person who raised it.
     *
     * The service refuses self-approval, so this code cannot appear for a
     * self-attested closure — which is what makes the trail worth reading.
     */
    MANUFACTURING_CAPA_CLOSURE_APPROVED: 'manufacturing_capa_closure_approved',
    MANUFACTURING_EPR_SCOPE_DETERMINED: 'manufacturing_epr_scope_determined',
    MANUFACTURING_EPR_VOLUME_RECORDED: 'manufacturing_epr_volume_recorded',
    MANUFACTURING_EPR_VOLUME_UPDATED: 'manufacturing_epr_volume_updated',
    MANUFACTURING_EPR_FULFILMENT_RECORDED: 'manufacturing_epr_fulfilment_recorded',
    MANUFACTURING_EPR_FULFILMENT_UPDATED: 'manufacturing_epr_fulfilment_updated',
    MANUFACTURING_EPR_SUBMISSION_RECORDED: 'manufacturing_epr_submission_recorded',
    /**
     * The organisation filed with the authority, and the trail carries the figure
     * **it declared** — never one the platform computed, because the platform holds
     * no rate, no coefficient and no unit charge.
     *
     * There is deliberately no reconciliation code: the reconciliation is a GET
     * that stores nothing, so there is no act to record. An audit row for a read
     * would say the organisation looked, which is not what this trail is for.
     */
    MANUFACTURING_EPR_SUBMISSION_FILED: 'manufacturing_epr_submission_filed',
    MANUFACTURING_SUBSTANCE_RECORDED: 'manufacturing_substance_recorded',
    MANUFACTURING_SUBSTANCE_UPDATED: 'manufacturing_substance_updated',
    MANUFACTURING_COMPOSITION_RECORDED: 'manufacturing_composition_recorded',
    /**
     * A composition row removed.
     *
     * The only destructive act in this module, and it is recorded rather than
     * silent: "this product no longer declares that substance" is a change an
     * authority may ask about, and the row it refers to is gone.
     */
    MANUFACTURING_COMPOSITION_REMOVED: 'manufacturing_composition_removed',
    /**
     * Sprint 63.31.0 (#444, #445) — a person linked a chemical-traceability
     * declaration or termination record to incidents or sector licences, or
     * recorded its discovery or occurrence time. The prior and new values are on
     * the row; the platform assigns no code and chooses no anchor.
     */
    MANUFACTURING_CHEMICAL_TRACEABILITY_RECORD_UPDATED: 'manufacturing_chemical_traceability_record_updated',
    MANUFACTURING_OT_PROFILE_CREATED: 'manufacturing_ot_profile_created',
    MANUFACTURING_OT_PROFILE_UPDATED: 'manufacturing_ot_profile_updated',
    MANUFACTURING_OT_REMOTE_ACCESS_REQUESTED: 'manufacturing_ot_remote_access_requested',
    /**
     * Approved — by somebody who is not the requester, and who is the acting user.
     *
     * Both refusals live on the service method, so this code cannot appear for a
     * self-approval or for an approval recorded on somebody else's behalf.
     */
    MANUFACTURING_OT_REMOTE_ACCESS_APPROVED: 'manufacturing_ot_remote_access_approved',
    /**
     * Revoked, which is a **decision** — and distinct from expiry, which is not.
     *
     * There is no expiry code, because nothing happens at expiry: the state is
     * derived from the clock at read time, so an audit row would be a claim that
     * the platform acted when it did not.
     */
    MANUFACTURING_OT_REMOTE_ACCESS_REVOKED: 'manufacturing_ot_remote_access_revoked',
    // ── Sprint 62.9.0 — logistics, customs and parcel operations ────
    //
    // Declared before any stream starts, because 62.8.0's first broadcast was
    // exactly this: four backend streams independently needed audit codes, all
    // four correctly stopped rather than edit a contract file, and 17 of the 26
    // type errors at first integration came from that one cause.
    //
    // Same naming rule: **what the organisation did**, not what changed in a row.
    // A customs record moving to `lodged` is `..._LODGED` — a statement was made
    // to an authority and by whom — not `..._UPDATED`, which says a column moved.
    // The three acts that reach outside the organisation (`_LODGED`,
    // `_AUTHORISATION_SUBMITTED`, `_CLAIM_DECIDED`) each have their own code for
    // the same reason they each have their own permission.
    LOGISTICS_SHIPMENT_REGISTERED: 'logistics_shipment_registered',
    LOGISTICS_SHIPMENT_UPDATED: 'logistics_shipment_updated',
    LOGISTICS_SHIPMENT_CLOSED: 'logistics_shipment_closed',
    LOGISTICS_SHIPMENT_CANCELLED: 'logistics_shipment_cancelled',
    LOGISTICS_CARGO_ITEM_ADDED: 'logistics_cargo_item_added',
    LOGISTICS_CARGO_ITEM_UPDATED: 'logistics_cargo_item_updated',
    LOGISTICS_CARGO_ITEM_REMOVED: 'logistics_cargo_item_removed',
    LOGISTICS_CUSTOMS_RECORD_CREATED: 'logistics_customs_record_created',
    LOGISTICS_CUSTOMS_RECORD_UPDATED: 'logistics_customs_record_updated',
    /** The artefact reached an authority. Its own code, its own permission. */
    LOGISTICS_CUSTOMS_RECORD_LODGED: 'logistics_customs_record_lodged',
    LOGISTICS_CUSTOMS_RECORD_AMENDED: 'logistics_customs_record_amended',
    LOGISTICS_CUSTOMS_RECORD_CLOSED: 'logistics_customs_record_closed',
    LOGISTICS_CUSTOMS_RECORD_WITHDRAWN: 'logistics_customs_record_withdrawn',
    /**
     * The clearance ledger has **one** code and no update or delete code, because
     * the ledger has no update and no delete. A trail offering vocabulary for an
     * operation the module cannot perform is vocabulary that reads as coverage.
     */
    LOGISTICS_CLEARANCE_EVENT_APPENDED: 'logistics_clearance_event_appended',
    LOGISTICS_CORRESPONDENCE_LINKED: 'logistics_correspondence_linked',
    LOGISTICS_CORRESPONDENCE_UPDATED: 'logistics_correspondence_updated',
    LOGISTICS_PARCEL_REGISTERED: 'logistics_parcel_registered',
    LOGISTICS_PARCEL_UPDATED: 'logistics_parcel_updated',
    LOGISTICS_PARCEL_CLOSED: 'logistics_parcel_closed',
    /**
     * Two codes for the parcel timeline, and again no update or delete.
     *
     * `_CORRECTED` is distinct from `_APPENDED` because a correction is a
     * statement that an earlier record was wrong — the fact a loss investigation
     * looks for — and folding it into the ordinary append code would make the one
     * event worth finding indistinguishable from the thousands that are not.
     */
    LOGISTICS_PARCEL_EVENT_APPENDED: 'logistics_parcel_event_appended',
    LOGISTICS_PARCEL_EVENT_CORRECTED: 'logistics_parcel_event_corrected',
    LOGISTICS_DELIVERY_PROOF_CAPTURED: 'logistics_delivery_proof_captured',
    /**
     * Changing when a signature image or a doorstep photograph will be deleted is
     * a privacy decision, not an edit. It gets its own code so the question "who
     * extended the retention on this material, and when" is answerable from the
     * trail rather than by diffing two versions of a row.
     */
    LOGISTICS_DELIVERY_PROOF_RETENTION_SET: 'logistics_delivery_proof_retention_set',
    /**
     * Sprint 63.1.0 — the organisation's position on when a parcel event's
     * evidence is deleted, stated at append.
     *
     * It has its own code rather than riding on `PARCEL_EVENT_APPENDED` for the
     * same reason its delivery-proof sibling above does: "who decided this
     * material's fate, and when" is a question an inspector asks directly, and an
     * answer that has to be reconstructed by diffing an append's detail blob is
     * not an answer.
     */
    LOGISTICS_PARCEL_EVENT_EVIDENCE_RETENTION_SET: 'logistics_parcel_event_evidence_retention_set',
    LOGISTICS_CLAIM_RAISED: 'logistics_claim_raised',
    LOGISTICS_CLAIM_UPDATED: 'logistics_claim_updated',
    /** A position taken toward a customer, with a liability behind it. */
    LOGISTICS_CLAIM_DECIDED: 'logistics_claim_decided',
    LOGISTICS_AUTHORISATION_CREATED: 'logistics_authorisation_created',
    LOGISTICS_AUTHORISATION_UPDATED: 'logistics_authorisation_updated',
    /** Filed with the authority. Its own code, its own permission. */
    LOGISTICS_AUTHORISATION_SUBMITTED: 'logistics_authorisation_submitted',
    LOGISTICS_AUTHORISATION_IN_FORCE: 'logistics_authorisation_in_force',
    LOGISTICS_AUTHORISATION_SUPERSEDED: 'logistics_authorisation_superseded',
    LOGISTICS_AUTHORISATION_WITHDRAWN: 'logistics_authorisation_withdrawn',
    LOGISTICS_EXPORTED: 'logistics_exported',
    // ── Sprint 62.13.0 — penalty exposure + enforcement cases (decree-wave stream) ──
    /** The org's declared exposure facts changed — entity basis, metrics, area marks. */
    PENALTY_EXPOSURE_INPUTS_UPDATED: 'penalty_exposure_inputs_updated',
    PENALTY_EXPOSURE_EXPORTED: 'penalty_exposure_exported',
    ENFORCEMENT_CASE_CREATED: 'enforcement_case_created',
    ENFORCEMENT_CASE_UPDATED: 'enforcement_case_updated',
    /**
     * A lifecycle stage transition is its own code: the register's value in an
     * inspection is the ability to answer "when did the decision stage begin and
     * who moved it there" from the trail, not from diffing two versions.
     */
    ENFORCEMENT_CASE_STAGE_CHANGED: 'enforcement_case_stage_changed',
    /** A corrective-measure execution entry, each carrying evidence references. */
    ENFORCEMENT_CASE_MEASURE_RECORDED: 'enforcement_case_measure_recorded',
    ENFORCEMENT_CASE_CLOSED: 'enforcement_case_closed',
    ENFORCEMENT_CASE_EXPORTED: 'enforcement_case_exported',
    // ── Sprint 62.14.0 — authority directions, legal holds, regulated accounts ──
    AUTHORITY_DIRECTION_CREATED: 'authority_direction_created',
    AUTHORITY_DIRECTION_UPDATED: 'authority_direction_updated',
    AUTHORITY_DIRECTION_CLASSIFIED: 'authority_direction_classified',
    AUTHORITY_DIRECTION_CONDITION_ATTESTED: 'authority_direction_condition_attested',
    AUTHORITY_DIRECTION_REMEDIATION_PLAN_RECORDED: 'authority_direction_remediation_plan_recorded',
    AUTHORITY_DIRECTION_OUTCOME_REPORTED: 'authority_direction_outcome_reported',
    AUTHORITY_DIRECTION_LEGAL_REVIEW_RECORDED: 'authority_direction_legal_review_recorded',
    AUTHORITY_DIRECTION_DATA_RELEASE_ATTEMPTED: 'authority_direction_data_release_attempted',
    AUTHORITY_DIRECTION_DATA_RELEASED: 'authority_direction_data_released',
    PRODUCT_CONFORMITY_CERTIFICATE_RECORDED: 'product_conformity_certificate_recorded',
    /** A response entry recorded against a direction, with its evidence refs. */
    AUTHORITY_DIRECTION_RESPONSE_RECORDED: 'authority_direction_response_recorded',
    AUTHORITY_DIRECTION_CLOSED: 'authority_direction_closed',
    /**
     * Sprint 62.26.0 (OD11) — an authority-stated due date recorded (or replaced)
     * on a direction whose clock states no numeric period. Its own code: the
     * record is a transcription of the authority's date, with its evidence and
     * recorder, and an inspection asks who entered it and from what document.
     */
    AUTHORITY_DIRECTION_STATED_DUE_RECORDED: 'authority_direction_stated_due_recorded',
    AUTHORITY_DIRECTION_EXPORTED: 'authority_direction_exported',
    LEGAL_HOLD_PLACED: 'legal_hold_placed',
    LEGAL_HOLD_RELEASED: 'legal_hold_released',
    LEGAL_HOLD_EXPORTED: 'legal_hold_exported',
    /**
     * A destruction attempt was refused because an active hold matched the
     * target. Its own code, because the refusal — not the eventual deletion —
     * is what an inspection asks this register to prove.
     */
    DESTRUCTION_BLOCKED_BY_HOLD: 'destruction_blocked_by_hold',
    REGULATED_ACCOUNT_CREATED: 'regulated_account_created',
    REGULATED_ACCOUNT_UPDATED: 'regulated_account_updated',
    /** One lifecycle event appended (kind carried in the event detail). */
    REGULATED_ACCOUNT_EVENT_APPENDED: 'regulated_account_event_appended',
    /** A correction event appended; the corrected event is superseded, never edited. */
    REGULATED_ACCOUNT_EVENT_CORRECTED: 'regulated_account_event_corrected',
    REGULATED_ACCOUNT_EXPORTED: 'regulated_account_exported',
    // ── fix-sprint-20260921-2110 — continuity plans and exercises (IRP-01) ──
    /*
     * The module shipped in 65.18.0 with no audit writer at all: five mutations
     * wrote `grc_continuity_plans` and `grc_continuity_exercises` and left no row
     * behind. The compliance outbox fact `complete()` raises is the obligation
     * TRIGGER, not the audit trail — it fires on one of the five and records a
     * duty rather than an act.
     */
    CONTINUITY_PLAN_CREATED: 'continuity_plan_created',
    CONTINUITY_EXERCISE_SCHEDULED: 'continuity_exercise_scheduled',
    CONTINUITY_EXERCISE_COMPLETED: 'continuity_exercise_completed',
    /**
     * A scheduled exercise that will not be held. Its own code, because an
     * exercise that never happened is not an exercise with a bad outcome, and
     * who cancelled it and why is what the register is asked to show.
     */
    CONTINUITY_EXERCISE_CANCELLED: 'continuity_exercise_cancelled',
    /** One improvement action's loop closed, with the person who attested it. */
    CONTINUITY_IMPROVEMENT_ACTION_RETESTED: 'continuity_improvement_action_retested',
    // ── fix-sprint-20260411-0657-followup F1 — Agent Lobby credentials and appointments ──
    /** A user holding agent_lobby:manage issued a lobby join token (agent or operator scope). */
    AGENT_LOBBY_JOIN_TOKEN_ISSUED: 'agent_lobby_join_token_issued',
    /** A join token was revoked; connections that presented it were closed. */
    AGENT_LOBBY_JOIN_TOKEN_REVOKED: 'agent_lobby_join_token_revoked',
    /** A Boss Agent was appointed — over REST by a user, or over the socket by an operator or the outgoing Boss. */
    AGENT_LOBBY_BOSS_APPOINTED: 'agent_lobby_boss_appointed',
    /** An agent's Agent OS role was changed over the lobby socket. */
    AGENT_LOBBY_ROLE_CHANGED: 'agent_lobby_role_changed',
    // ── Sprint 62.24.0 — the credit-information supply register ──
    CREDIT_INFORMATION_SUPPLY_CREATED: 'credit_information_supply_created',
    /** Cover, transfer reference, withdrawal determination and notes changes on a supply row. */
    CREDIT_INFORMATION_SUPPLY_UPDATED: 'credit_information_supply_updated',
    CREDIT_INFORMATION_SUPPLY_EXPORTED: 'credit_information_supply_exported',
    // ── fix-sprint-20260922-2142 F6 — the credit-information use register (FINA-01) ──
    /**
     * A use was recorded because the consent it cites covered it. Refusals write
     * no row and no event: the refusal IS the response, and a use the instrument
     * prohibits is not an act the organisation performed on the register.
     */
    CREDIT_INFORMATION_USE_RECORDED: 'credit_information_use_recorded',
    /** The consent a recorded use rested on was recorded as withdrawn — with who recorded it. */
    CREDIT_INFORMATION_USE_CONSENT_WITHDRAWN: 'credit_information_use_consent_withdrawn',
    // ── fix-sprint-20260922-2142 F6 — the legal-matter register (LEG-01) ──
    /**
     * Four acts, four codes. A matter received, acknowledged to its raiser,
     * withdrawn by its raiser and resolved by a named person are different
     * things to whoever reads the trail — the withdrawal in particular carries
     * the operator's reason, which a single `legal_matter_updated` would bury.
     */
    LEGAL_MATTER_RECEIVED: 'legal_matter_received',
    LEGAL_MATTER_ACKNOWLEDGED: 'legal_matter_acknowledged',
    LEGAL_MATTER_WITHDRAWN: 'legal_matter_withdrawn',
    LEGAL_MATTER_RESOLVED: 'legal_matter_resolved',
    /**
     * Sprint 63.31.0 (owner ruling OD-E; gate E12.5 pass 2, P3-L2) — a person
     * accepted or dismissed the suggestion that a matter opened from an intake is
     * the same matter as an open one opened from an earlier intake that gave the
     * same email address. Intakes are never joined by the platform; opening a
     * matter from an intake writes `legal_matter_received` with the link (and
     * any suggestions) in its payload.
     */
    LEGAL_MATTER_SUGGESTED_LINK_DECIDED: 'legal_matter_suggested_link_decided',
    /**
     * Sprint 63.31.0 (gate E12.5 pass 2, P2-L2) — a person recorded the right a
     * matter opened from an intake rests on, which the intake left unnamed.
     */
    LEGAL_MATTER_RIGHT_RECORDED: 'legal_matter_right_recorded',
    // ── fix-sprint-20260921-2110 — de-identification runs (toolkit row ANON-01) ──
    DEIDENTIFICATION_RUN_CREATED: 'deidentification_run_created',
    /** Population, method, sample outcome and the residual-risk judgement on a draft run. */
    DEIDENTIFICATION_RUN_UPDATED: 'deidentification_run_updated',
    /**
     * Approved or rejected. Its own code, and the reason the module needed an
     * audit writer at all: accepting residual re-identification risk is fenced
     * behind its own permission because it is a separate attributable act, and
     * `decided_by` / `decided_at` on the row are editable state rather than an
     * immutable chained entry.
     */
    DEIDENTIFICATION_RUN_DECIDED: 'deidentification_run_decided',
    // ── Sprint 62.19.0 — sector licences, marketing-claim gate, investor protection ──
    SECTOR_LICENCE_CREATED: 'sector_licence_created',
    /** Status, expiry, scope and evidence changes on a licence — each is what the gate decides over. */
    SECTOR_LICENCE_UPDATED: 'sector_licence_updated',
    MARKETING_APPROVAL_CREATED: 'marketing_approval_created',
    /** The gate passed; the licence reference is attached to the approval. */
    MARKETING_APPROVAL_APPROVED: 'marketing_approval_approved',
    /**
     * The gate REFUSED. Its own code, because the refusal — recorded with who
     * attempted it and the pack provision it cited — is what an inspection asks
     * this register to prove, not the eventual approval.
     */
    MARKETING_APPROVAL_REFUSED: 'marketing_approval_refused',
    MARKETING_APPROVAL_WITHDRAWN: 'marketing_approval_withdrawn',
    // ── Sprint 62.54.0 — fee lines on applications, the decision kept apart ──
    LICENCE_FEE_APPLICATION_CREATED: 'licence_fee_application_created',
    LICENCE_FEE_APPLICATION_UPDATED: 'licence_fee_application_updated',
    /**
     * The authority's decision on an application, as a person recorded it. Its
     * own code and its own route (AC6): no fee-line write emits it.
     */
    LICENCE_FEE_APPLICATION_DECISION_RECORDED: 'licence_fee_application_decision_recorded',
    FEE_LINE_RECORDED: 'fee_line_recorded',
    FEE_LINE_UPDATED: 'fee_line_updated',
    FEE_LINE_DELETED: 'fee_line_deleted',
    /**
     * A fee-line write that had to be taken back (the record's procedure or
     * filing date moved, or another line of the record was saved at the same
     * moment) could not be, because a third save changed the line first. The
     * save was refused; this row names what it had set, so the record says what
     * may still stand (fix-sprint-20261023-2300, Finding 2).
     */
    FEE_LINE_TAKE_BACK_MISSED: 'fee_line_take_back_missed',
    SENDER_NAME_CERTIFICATE_CREATED: 'sender_name_certificate_created',
    SENDER_NAME_CERTIFICATE_UPDATED: 'sender_name_certificate_updated',
    SENDER_NAME_CERTIFICATE_DELETED: 'sender_name_certificate_deleted',
    /**
     * Sprint 62.55.0 (UC-VN-109 F-1, #684) — fee lines and the decisions kept
     * apart from them exported through UnifiedExportService (R-33), for one
     * application, one certificate or one paid-on month; the scope, format,
     * locale, file id and line count are in the detail.
     */
    FEE_LINES_EXPORTED: 'fee_lines_exported',
    INVESTOR_VERIFICATION_RECORDED: 'investor_verification_recorded',
    INVESTOR_VERIFICATION_UPDATED: 'investor_verification_updated',
    ACCOUNT_DATA_DISCLOSURE_RECORDED: 'account_data_disclosure_recorded',
    /**
     * A read of the account-data disclosure register — list or single record.
     * Reading the register is itself an access to account-data governance
     * records, and "every access is on the audit trail" is the duty evidenced.
     */
    ACCOUNT_DATA_DISCLOSURE_ACCESSED: 'account_data_disclosure_accessed',
    REGULATORY_DISCLOSURE_CREATED: 'regulatory_disclosure_created',
    /** A new version appended; the record returns to draft and its approver is cleared. */
    REGULATORY_DISCLOSURE_VERSION_ADDED: 'regulatory_disclosure_version_added',
    REGULATORY_DISCLOSURE_APPROVED: 'regulatory_disclosure_approved',
    REGULATORY_DISCLOSURE_PUBLISHED: 'regulatory_disclosure_published',
    // ── Sprint 62.24.0 — data-service provider records (notification, periodic and ad-hoc reports) ──
    DATA_SERVICE_PROVIDER_CREATED: 'data_service_provider_created',
    DATA_SERVICE_PROVIDER_UPDATED: 'data_service_provider_updated',
    /** One notification, periodic-report or ad-hoc-report entry appended (kind carried in the detail). */
    DATA_SERVICE_PROVIDER_ENTRY_RECORDED: 'data_service_provider_entry_recorded',
    DATA_SERVICE_PROVIDER_EXPORTED: 'data_service_provider_exported',
    // ── Sprint 62.50.0 — supervision-information records ──
    // One created / updated pair per register; the record kind is the entity type.
    // A bind or unbind of evidence files is its own event, the file ids in the detail.
    PCF_SUPERVISION_RECORD_CREATED: 'pcf_supervision_record_created',
    PCF_SUPERVISION_RECORD_UPDATED: 'pcf_supervision_record_updated',
    PCF_SUPERVISION_EVIDENCE_BOUND: 'pcf_supervision_evidence_bound',
    PCF_SUPERVISION_EVIDENCE_UNBOUND: 'pcf_supervision_evidence_unbound',
    // Sprint 62.50.1 — one register exported through UnifiedExportService (R-33);
    // the surface, format, locale, file id and record count are in the detail.
    PCF_SUPERVISION_REGISTER_EXPORTED: 'pcf_supervision_register_exported',
    // ── Sprint 62.51.0 — data-exploitation records ──
    // One created / updated pair across every register; the record kind is the
    // entity type. A sub-record appended to a record (a risk-plan version, the
    // extension, a Centre action, a certificate, a reuse check, the end of a
    // purpose) is its own event, the sub-record kind in the detail. A bind or
    // unbind of evidence files is its own event, the file ids in the detail.
    DATA_EXPLOITATION_RECORD_CREATED: 'data_exploitation_record_created',
    DATA_EXPLOITATION_RECORD_UPDATED: 'data_exploitation_record_updated',
    DATA_EXPLOITATION_SUBRECORD_RECORDED: 'data_exploitation_subrecord_recorded',
    // Remediation Y — an append-only entry marked recorded in error by the
    // organisation (its own correction, never an authority's act); the
    // sub-record kind and id in the detail, never the reason's text.
    DATA_EXPLOITATION_SUBRECORD_MARKED_IN_ERROR: 'data_exploitation_subrecord_marked_in_error',
    DATA_EXPLOITATION_EVIDENCE_BOUND: 'data_exploitation_evidence_bound',
    DATA_EXPLOITATION_EVIDENCE_UNBOUND: 'data_exploitation_evidence_unbound',
    // One register, or one grant with its resting records, exported through
    // UnifiedExportService (R-33); surface, format, locale, file id and count in the detail.
    DATA_EXPLOITATION_EXPORTED: 'data_exploitation_exported',
    // ── Sprint 62.52.0 — cybersecurity assurance index records ──
    // Owner answer OQ-2 (a): every read and every denied read is audited, and a
    // row carries ids, codes and field NAMES only — never a Mức, an explanation,
    // a figure or any text a person typed. One created / updated pair across the
    // four record families (the family is the entity type).
    CYBERSECURITY_ASSURANCE_INDEX_RECORD_CREATED: 'cybersecurity_assurance_index_record_created',
    CYBERSECURITY_ASSURANCE_INDEX_RECORD_UPDATED: 'cybersecurity_assurance_index_record_updated',
    // A read of a period's records by a member or a restricted-read holder; the route in the detail.
    CYBERSECURITY_ASSURANCE_INDEX_RECORD_READ: 'cybersecurity_assurance_index_record_read',
    // A read refused with the one 404 (not a member, no restricted read, or no such id).
    CYBERSECURITY_ASSURANCE_INDEX_READ_DENIED: 'cybersecurity_assurance_index_read_denied',
    CYBERSECURITY_ASSURANCE_INDEX_MEMBERS_CHANGED: 'cybersecurity_assurance_index_members_changed',
    CYBERSECURITY_ASSURANCE_INDEX_PERIOD_CLOSED: 'cybersecurity_assurance_index_period_closed',
    // A system's level re-read from its classification profile by a person (CL-2).
    CYBERSECURITY_ASSURANCE_INDEX_CLASSIFICATION_REREAD: 'cybersecurity_assurance_index_classification_reread',
    CYBERSECURITY_ASSURANCE_INDEX_EVIDENCE_UPLOADED: 'cybersecurity_assurance_index_evidence_uploaded',
    CYBERSECURITY_ASSURANCE_INDEX_EVIDENCE_DOWNLOADED: 'cybersecurity_assurance_index_evidence_downloaded',
    CYBERSECURITY_ASSURANCE_INDEX_EVIDENCE_BOUND: 'cybersecurity_assurance_index_evidence_bound',
    CYBERSECURITY_ASSURANCE_INDEX_EVIDENCE_UNBOUND: 'cybersecurity_assurance_index_evidence_unbound',
    // One period exported through UnifiedExportService (R-33); format, locale, file id and row count.
    CYBERSECURITY_ASSURANCE_INDEX_EXPORTED: 'cybersecurity_assurance_index_exported',
    // ── Sprint 62.49.0 — food traceability records ──
    // One created / updated pair across every register; the record family is the
    // entity type. A row carries ids, codes and field NAMES only — never a
    // complainant's contact, a result's text, a decision's wording or any other
    // text a person typed. A sub-record appended to a record (a certificate
    // renewal, a testing-body statement, an inspection response, a notice's
    // sending, a quantity report, a decision event, a receipt, a review decision,
    // a complaint's investigation decision or closure) is its own event, the
    // sub-record kind in the detail.
    FOOD_TRACEABILITY_RECORD_CREATED: 'food_traceability_record_created',
    FOOD_TRACEABILITY_RECORD_UPDATED: 'food_traceability_record_updated',
    FOOD_TRACEABILITY_SUBRECORD_RECORDED: 'food_traceability_subrecord_recorded',
    // A second person's approval (equivalence judgement, testing-body statement,
    // inspection response, correction request, grant, review decision); the
    // approval kind and the record id in the detail, the note's presence only.
    FOOD_TRACEABILITY_APPROVED: 'food_traceability_approved',
    // An approval refused because the approver entered the record, so an attempt is visible.
    FOOD_TRACEABILITY_SELF_APPROVAL_REFUSED: 'food_traceability_self_approval_refused',
    // A correction request approved and applied to the trace ledger or the batch
    // under the approver (the appended event or the version row in the detail),
    // or rejected by the approver.
    FOOD_TRACEABILITY_CORRECTION_APPLIED: 'food_traceability_correction_applied',
    FOOD_TRACEABILITY_CORRECTION_REJECTED: 'food_traceability_correction_rejected',
    // A direct correction to a batch or trace event a food record references,
    // refused with 409 `FOOD_TRACEABILITY_CORRECTION_APPROVAL_REQUIRED`.
    FOOD_TRACEABILITY_DIRECT_CORRECTION_REFUSED: 'food_traceability_direct_correction_refused',
    // A food grant's role applied on approval, and removed; the role assignment's
    // own audit rows are written beside these by the user service (AC25 holds the
    // grant's dates equal to them).
    FOOD_TRACEABILITY_GRANT_APPLIED: 'food_traceability_grant_applied',
    FOOD_TRACEABILITY_GRANT_REMOVED: 'food_traceability_grant_removed',
    FOOD_TRACEABILITY_EVIDENCE_BOUND: 'food_traceability_evidence_bound',
    FOOD_TRACEABILITY_EVIDENCE_UNBOUND: 'food_traceability_evidence_unbound',
    // One register exported through UnifiedExportService (R-33), or a recall
    // case's internal report copy rendered; surface or copy, format, locale, file
    // id and record count in the detail.
    FOOD_TRACEABILITY_EXPORTED: 'food_traceability_exported',
    // ── Sprint 62.55.0 — customs tax-administration records (stream B1) ──
    // One created / updated pair across every register; the record family is the
    // entity type. A bind or unbind of evidence files is its own event, the file
    // ids in the detail. An export is its own event, kind and format in the detail.
    CUSTOMS_TAX_RECORD_CREATED: 'customs_tax_record_created',
    CUSTOMS_TAX_RECORD_UPDATED: 'customs_tax_record_updated',
    CUSTOMS_TAX_EVIDENCE_BOUND: 'customs_tax_evidence_bound',
    CUSTOMS_TAX_EVIDENCE_UNBOUND: 'customs_tax_evidence_unbound',
    CUSTOMS_TAX_EXPORTED: 'customs_tax_exported',
    // Owner decision O7 (2026-10-26) — the optional review step: an approval given
    // (the version it was given against in the detail; the note's presence, never
    // its words), an approval withdrawn by its approver, and a self-approval
    // refused (the reasons in the detail), so an attempt is visible.
    CUSTOMS_TAX_REVIEW_APPROVED: 'customs_tax_review_approved',
    CUSTOMS_TAX_REVIEW_WITHDRAWN: 'customs_tax_review_withdrawn',
    CUSTOMS_TAX_REVIEW_SELF_APPROVAL_REFUSED: 'customs_tax_review_self_approval_refused',
    // ── Sprint 62.56.0 — biometric capture components, custody, readiness ──
    // One row per write: the record kind is the entity type; the detail carries
    // `{fields, changes}` with free text withheld, and the file ids a write bound
    // or unbound in the same row.
    BIOMETRIC_CAPTURE_RECORD_CREATED: 'biometric_capture_record_created',
    BIOMETRIC_CAPTURE_RECORD_UPDATED: 'biometric_capture_record_updated',
    BIOMETRIC_CAPTURE_LINK_ADDED: 'biometric_capture_link_added',
    BIOMETRIC_CAPTURE_LINK_REMOVED: 'biometric_capture_link_removed',
    BIOMETRIC_CAPTURE_EXPORTED: 'biometric_capture_exported',
    // ── Sprint 63.24.0 — controlled-testing case (Slice A) ──
    CONTROLLED_TESTING_CASE_OPENED: 'controlled_testing_case_opened',
    CONTROLLED_TESTING_CASE_UPDATED: 'controlled_testing_case_updated',
    /** An authority step recorded against the case; the state is in the detail. */
    CONTROLLED_TESTING_TRANSITION_RECORDED: 'controlled_testing_transition_recorded',
    /**
     * A requested step the installed procedure does not declare, refused. Audited
     * so an attempt to record a state the authority never reached is visible.
     */
    CONTROLLED_TESTING_TRANSITION_REFUSED: 'controlled_testing_transition_refused',
    // Sprint 63.24.0 Slice B — a person's qualification record and its evidence.
    // The person's name is never written to a detail; the role codes and counts are.
    CONTROLLED_TESTING_PERSONNEL_RECORDED: 'controlled_testing_personnel_recorded',
    CONTROLLED_TESTING_PERSONNEL_UPDATED: 'controlled_testing_personnel_updated',
    CONTROLLED_TESTING_PERSONNEL_EVIDENCE_ADDED: 'controlled_testing_personnel_evidence_added',
    // Sprint 63.24.0 Slice C — the solution record and its technical statement. The
    // customer group's text is never written to a detail; the codes and the field names are.
    CONTROLLED_TESTING_SOLUTION_RECORDED: 'controlled_testing_solution_recorded',
    CONTROLLED_TESTING_SOLUTION_UPDATED: 'controlled_testing_solution_updated',
    CONTROLLED_TESTING_SOLUTION_EVIDENCE_ADDED: 'controlled_testing_solution_evidence_added',
    // Sprint 63.24.0 Slice D — the authoritative-source record of a case: a row recorded, changed, or given a supplement file.
    CONTROLLED_TESTING_REGISTRY_RECORDED: 'controlled_testing_registry_recorded',
    CONTROLLED_TESTING_REGISTRY_UPDATED: 'controlled_testing_registry_updated',
    CONTROLLED_TESTING_REGISTRY_EVIDENCE_ADDED: 'controlled_testing_registry_evidence_added',
    /** Sprint 63.24.0 Slice E: a child case (adjustment or extension) opened on a granted parent. */
    CONTROLLED_TESTING_CHILD_CASE_OPENED: 'controlled_testing_child_case_opened',
    /** A child case refused: no declared grant, no declared kind, or a parent not operational. Audited. */
    CONTROLLED_TESTING_CHILD_CASE_REFUSED: 'controlled_testing_child_case_refused',
    /** An internal sign-off raised for a child case's filing (E4). */
    CONTROLLED_TESTING_APPROVAL_REQUESTED: 'controlled_testing_approval_requested',
    /** The organisation's filing recorded, against an approved sign-off (E4). */
    CONTROLLED_TESTING_FILING_RECORDED: 'controlled_testing_filing_recorded',
    /** A filing refused: its approval is missing, not approved, or for another case. Audited. */
    CONTROLLED_TESTING_FILING_REFUSED: 'controlled_testing_filing_refused',
    // Sprint 63.24.0 Slice F — stop, completion, closure and direction records (F1–F6).
    /** A file the stop rests on is held under the case. Audited with the file id, never the text. */
    CONTROLLED_TESTING_STOP_EVIDENCE_ADDED: 'controlled_testing_stop_evidence_added',
    /** The organisation's stop filed, with its three files and the stop date a person entered (F2). */
    CONTROLLED_TESTING_STOP_FILED: 'controlled_testing_stop_filed',
    /** A stop refused: a file is not held under the case, or the stop is already filed. Audited (F2). */
    CONTROLLED_TESTING_STOP_REFUSED: 'controlled_testing_stop_refused',
    /** The authority's decision to stop and revoke recorded from its document (F3). */
    CONTROLLED_TESTING_REVOCATION_RECORDED: 'controlled_testing_revocation_recorded',
    /** A file the completion rests on is held under the case. */
    CONTROLLED_TESTING_COMPLETION_EVIDENCE_ADDED: 'controlled_testing_completion_evidence_added',
    /** The organisation's completion request filed with its two reports (F4). */
    CONTROLLED_TESTING_COMPLETION_FILED: 'controlled_testing_completion_filed',
    /** A completion or certificate refused because the authority's stop-and-revoke decision is recorded (Finding 12). */
    CONTROLLED_TESTING_COMPLETION_REFUSED: 'controlled_testing_completion_refused',
    /** The authority's copy of the certificate recorded from its source (F4). */
    CONTROLLED_TESTING_CERTIFICATE_RECORDED: 'controlled_testing_certificate_recorded',
    /** A stop notice or the liquidation record recorded (F5). */
    CONTROLLED_TESTING_CLOSURE_RECORDED: 'controlled_testing_closure_recorded',
    /** One post-stop anchor date entered on the closure record, audited by field name, never by value (owner ruling 2026-10-11). */
    CONTROLLED_TESTING_CLOSURE_ANCHOR_RECORDED: 'controlled_testing_closure_anchor_recorded',
    CONTROLLED_TESTING_CLOSURE_ANCHOR_CORRECTED: 'controlled_testing_closure_anchor_corrected',
    /** A State Bank request or on-site notice recorded in the direction register (F6). */
    CONTROLLED_TESTING_DIRECTION_RECORDED: 'controlled_testing_direction_recorded',
    // ── Sprint 62.38.0 — Supervisory Reporting register ──
    SUPERVISORY_REPORTING_PROFILE_UPDATED: 'supervisory_reporting_profile_updated',
    SUPERVISORY_INDICATOR_CREATED: 'supervisory_indicator_created',
    SUPERVISORY_INDICATOR_UPDATED: 'supervisory_indicator_updated',
    /** A named person approved an indicator's lineage. */
    SUPERVISORY_INDICATOR_LINEAGE_APPROVED: 'supervisory_indicator_lineage_approved',
    SUPERVISORY_SUBMISSION_CHECK_CREATED: 'supervisory_submission_check_created',
    SUPERVISORY_SUBMISSION_CHECK_UPDATED: 'supervisory_submission_check_updated',
    SUPERVISORY_SUBMISSION_CHECK_APPROVED: 'supervisory_submission_check_approved',
    SUPERVISORY_DATA_CORRECTION_RECORDED: 'supervisory_data_correction_recorded',
    SUPERVISORY_DATA_CORRECTION_UPDATED: 'supervisory_data_correction_updated',
    SUPERVISORY_INSPECTION_RECORDED: 'supervisory_inspection_recorded',
    SUPERVISORY_INSPECTION_UPDATED: 'supervisory_inspection_updated',
    SUPERVISORY_INSPECTION_CLOSED: 'supervisory_inspection_closed',
    // Sprint 62.45.0 — interruption notices, change and contract notices, evidence packs.
    SUPERVISORY_INTERRUPTION_NOTICE_RECORDED: 'supervisory_interruption_notice_recorded',
    SUPERVISORY_INTERRUPTION_NOTICE_UPDATED: 'supervisory_interruption_notice_updated',
    SUPERVISORY_EVENT_NOTICE_RECORDED: 'supervisory_event_notice_recorded',
    SUPERVISORY_EVENT_NOTICE_UPDATED: 'supervisory_event_notice_updated',
    /** A reporting-period evidence pack exported through UnifiedExportService. */
    SUPERVISORY_PERIOD_EXPORTED: 'supervisory_period_exported',
    /** An inspection evidence pack exported through UnifiedExportService. */
    SUPERVISORY_INSPECTION_EXPORTED: 'supervisory_inspection_exported',
    // Sprint 62.45.0 Slice H — the submission recorded on a check without
    // reopening its approval, and recommendations from supervision letters.
    /** A check's submission record written (draft or approved), with the prior values. */
    SUPERVISORY_CHECK_SUBMISSION_RECORDED: 'check_submission_recorded',
    SUPERVISORY_RECOMMENDATION_RECORDED: 'supervisory_recommendation_recorded',
    SUPERVISORY_RECOMMENDATION_UPDATED: 'supervisory_recommendation_updated',
    // ── Sprint 62.39.0 — Cross-Border Identity Exchange register (elective) ──
    IDENTITY_EXCHANGE_CREATED: 'identity_exchange_created',
    IDENTITY_EXCHANGE_UPDATED: 'identity_exchange_updated',
    /** A status change; `production` only through the gate, with the approver recorded. */
    IDENTITY_EXCHANGE_STATUS_CHANGED: 'identity_exchange_status_changed',
    // ── Sprint 62.46.0 — the AML programme register ──
    //
    // Case rows carry only the case id and the transition in their details, never
    // a subject field (customer reference, account or wallet): audit rows are read
    // by `audit:read` holders who may hold no `aml_case:read`.
    AML_SCOPING_RECORDED: 'aml_scoping_recorded',
    /**
     * A scoping role that deactivates the programme (and so refuses new cases)
     * was recorded by a caller without `aml_case:approve`, and refused (gate
     * E12.5 pass 1, P1-1).
     */
    AML_SCOPING_DEACTIVATION_REFUSED: 'aml_scoping_deactivation_refused',
    /** The two-person rule turned on or off; turning it off carries a reason. */
    AML_TWO_PERSON_POLICY_CHANGED: 'aml_two_person_policy_changed',
    AML_RETENTION_SCOPE_RECORDED: 'aml_retention_scope_recorded',
    AML_RETENTION_SCOPE_UPDATED: 'aml_retention_scope_updated',
    AML_INDICATOR_ROW_UPDATED: 'aml_indicator_row_updated',
    AML_INDICATOR_ENTITY_ROW_RECORDED: 'aml_indicator_entity_row_recorded',
    /** A configuration version approved and frozen. */
    AML_INDICATOR_CONFIG_APPROVED: 'aml_indicator_config_approved',
    /**
     * Sprint 63.31.0 (#435) — the two-person rule refused an approver who edited
     * the draft since the latest approved version.
     */
    AML_INDICATOR_CONFIG_SELF_APPROVAL_REFUSED: 'aml_indicator_config_self_approval_refused',
    /** Sprint 63.31.0 (#436 L4) — review tasks raised on indicator rows from a launch assessment. */
    AML_INDICATOR_REVIEW_TASK_RAISED: 'aml_indicator_review_task_raised',
    /** Sprint 63.31.0 (#436 L4) — a person recorded the review a task asked for. */
    AML_INDICATOR_REVIEW_TASK_COMPLETED: 'aml_indicator_review_task_completed',
    /** Sprint 63.31.0 (#436 7a) — a person signed a records-retention scope entry. */
    AML_RETENTION_SCOPE_APPROVED: 'aml_retention_scope_approved',
    /** Sprint 63.31.0 (#436 7a) — the two-person rule refused a second signature by the same person. */
    AML_RETENTION_SCOPE_SELF_APPROVAL_REFUSED: 'aml_retention_scope_self_approval_refused',
    AML_CASE_OPENED: 'aml_case_opened',
    /** A subject or trigger edit before approval, with the previous values of the changed keys. */
    AML_CASE_UPDATED: 'aml_case_updated',
    /** Every read of one case — the restricted register's access history. */
    AML_CASE_VIEWED: 'aml_case_viewed',
    AML_CASE_LISTED: 'aml_case_listed',
    /**
     * A caller without `aml_case:read` asked for a case. Its own code: the
     * response is an indistinguishable 404, so this row is the only place the
     * attempt is visible.
     */
    AML_CASE_READ_DENIED: 'aml_case_read_denied',
    /** A person recorded the determination (or replaced one before approval). */
    AML_CASE_DETERMINED: 'aml_case_determined',
    AML_CASE_APPROVED: 'aml_case_approved',
    /** The two-person rule refused an approver who is the determiner. */
    AML_CASE_SELF_APPROVAL_REFUSED: 'aml_case_self_approval_refused',
    /**
     * Sprint 62.46.0 (gate E12.5 pass 2, P2-A) — the platform took back an
     * approval it had just written, because the two-person rule in force could
     * not be confirmed after the write (the second read of the setting failed).
     * The case is `determined` again; nothing was approved.
     */
    AML_CASE_APPROVAL_REVERTED: 'aml_case_approval_reverted',
    /** The reference of a report the organisation filed itself was recorded. The platform files nothing. */
    AML_CASE_FILING_RECORDED: 'aml_case_filing_recorded',
    AML_CASE_CLOSED: 'aml_case_closed',
    AML_CASE_CDD_REFRESH_REQUESTED: 'aml_case_cdd_refresh_requested',
    /**
     * Sprint 63.31.0 (#436 S13) — a related action (a postponement or a freeze
     * the organisation decided) recorded on a case. The platform takes none.
     */
    AML_CASE_RELATED_ACTION_RECORDED: 'aml_case_related_action_recorded',
    /**
     * Sprint 63.31.0 (UC-VN-48 S14) — a person recorded which installed start
     * event begins a case's record-retention period, and its date.
     */
    AML_CASE_RETENTION_START_RECORDED: 'aml_case_retention_start_recorded',
    /**
     * The diligence-side record of a CDD refresh. Carries NO case id, so a
     * diligence reader cannot learn from the trail that a case exists.
     */
    AML_DILIGENCE_REFRESH_REQUESTED: 'aml_diligence_refresh_requested',
    AML_BENEFICIAL_OWNER_RECORDED: 'aml_beneficial_owner_recorded',
    AML_BENEFICIAL_OWNER_UPDATED: 'aml_beneficial_owner_updated',
    AML_LAUNCH_RECORDED: 'aml_launch_recorded',
    AML_LAUNCH_SCOPE_DETERMINED: 'aml_launch_scope_determined',
    AML_LAUNCH_ASSESSMENT_RECORDED: 'aml_launch_assessment_recorded',
    AML_LAUNCH_ASSESSMENT_DECIDED: 'aml_launch_assessment_decided',
    AML_LAUNCH_APPROVED: 'aml_launch_approved',
    /**
     * The launch gate REFUSED. Its own code, because the refusal — who attempted
     * it, when, and the installed requirement it cites — is what the gate exists
     * to prove, not the eventual approval.
     */
    AML_LAUNCH_APPROVAL_REFUSED: 'aml_launch_approval_refused',
    /**
     * Sprint 63.31.0 (#435) — the two-person rule refused a launch approval by the
     * person who decided the permitting assessment.
     */
    AML_LAUNCH_SELF_APPROVAL_REFUSED: 'aml_launch_self_approval_refused',
    /** Sprint 63.31.0 (#436 L7) — a launch's post-launch review date set or cleared. */
    AML_LAUNCH_POST_LAUNCH_REVIEW_SET: 'aml_launch_post_launch_review_set',
    AML_INSPECTION_PACK_EXPORTED: 'aml_inspection_pack_exported',
    /**
     * Sprint 62.46.0 (gate E12.5 pass 3) — the access-log retention sweep's
     * account of the rows that record a read of a suspicious-activity case
     * (`grc_access_log` rows of `resource_type: 'aml_case'`).
     *
     * The sweep's own row (`ACCESS_LOG_RETENTION_SWEPT`) is read by every audit
     * reader, and the retention register shows a reader without `aml_case:read`
     * the position of the rows they may read. Had the sweep's row counted the
     * case reads too, the difference between the register the day before and the
     * row the day after would be the number of case reads that expired that day.
     * So the sweep's row counts the readable rows only, and the case reads are
     * accounted for here, a restricted type a reader without the grant is never
     * shown. The two rows together are the run's whole account.
     */
    AML_CASE_READS_RETENTION_SWEPT: 'aml_case_reads_retention_swept',
    // ── Sprint 62.18.0 — issued-record register, consent and sharing evidence ──
    ISSUED_RECORD_SCOPE_DECIDED: 'issued_record_scope_decided',
    ISSUED_RECORD_CREATED: 'issued_record_created',
    /** A version appended — first issue, reissuance or correction; never an edit. */
    ISSUED_RECORD_VERSION_ISSUED: 'issued_record_version_issued',
    /** The prior version's `superseded_by_version_id` claimed by compare-and-set. */
    ISSUED_RECORD_VERSION_SUPERSEDED: 'issued_record_version_superseded',
    ISSUED_RECORD_SHARED: 'issued_record_shared',
    /**
     * The sharing gate REFUSED. Its own code, because the refusal — recorded
     * with who attempted it and the restriction the pack states — is what an
     * inspection asks this register to prove, not the eventual sharing.
     */
    ISSUED_RECORD_SHARING_REFUSED: 'issued_record_sharing_refused',
    ISSUED_RECORD_CONSENT_RECORDED: 'issued_record_consent_recorded',
    ISSUED_RECORD_CONSENT_WITHDRAWN: 'issued_record_consent_withdrawn',
    ISSUED_RECORD_EVIDENCE_RECORDED: 'issued_record_evidence_recorded',
    /**
     * A read of a record or of its versions. The access history the duty
     * requires is built from these entries, so every read must leave one.
     */
    ISSUED_RECORD_ACCESSED: 'issued_record_accessed',
    ISSUED_RECORD_EXPORTED: 'issued_record_exported',
    /** A retention-driven deletion of an issued version refused while the pack states the rule. */
    ISSUED_RECORD_DELETION_REFUSED: 'issued_record_deletion_refused',
    // ── Sprint 62.17.0 — the publication register, the appointed-representative
    // register and the AI-assisted publication assessment ──
    PUBLICATION_CREATED: 'publication_created',
    PUBLICATION_UPDATED: 'publication_updated',
    /** Rights, licence or content-provenance evidence attached to the publication (AC8). */
    PUBLICATION_RIGHTS_EVIDENCE_RECORDED: 'publication_rights_evidence_recorded',
    /** A person applied a category, with the reason the tag carries (AC3). */
    PUBLICATION_CATEGORY_APPLIED: 'publication_category_applied',
    /** A category removed — it changes which assessment steps the screens show, so it is on the trail. */
    PUBLICATION_CATEGORY_REMOVED: 'publication_category_removed',
    /**
     * fix-sprint-20260904-2130-stream-remainders F1 (owner ruling Q-SR1, 2026-10-20) — a person
     * recorded one of the assessment steps the pack declares for an applied category,
     * with the date, the reason and the evidence.
     */
    PUBLICATION_CATEGORY_STEP_RECORDED: 'publication_category_step_recorded',
    PUBLICATION_PREREQUISITES_RECORDED: 'publication_prerequisites_recorded',
    /** A legal-deposit submission and its receipt reference recorded (AC1). */
    PUBLICATION_DEPOSIT_SUBMISSION_RECORDED: 'publication_deposit_submission_recorded',
    /** The release gate PASSED: the publication is marked releasable. */
    PUBLICATION_RELEASE_MARKED: 'publication_release_marked',
    /**
     * The release gate REFUSED. Its own code, because the refusal — recorded with
     * who attempted it, when, and the pack record it cites — is exactly what AC2
     * asks this register to prove, not the eventual release.
     */
    PUBLICATION_RELEASE_REFUSED: 'publication_release_refused',
    /** An entry appended to the appointed-representative register (AC4). */
    LEGAL_REPRESENTATIVE_RECORDED: 'legal_representative_recorded',
    /** The prior entry's `superseded_by_entry_id` claimed by compare-and-set (AC4). */
    LEGAL_REPRESENTATIVE_SUPERSEDED: 'legal_representative_superseded',
    PUBLICATION_ASSESSMENT_CREATED: 'publication_assessment_created',
    PUBLICATION_ASSESSMENT_UPDATED: 'publication_assessment_updated',
    /**
     * An assessment completed by its NAMED human approver (AC7). Never a ruling
     * on lawfulness — the record carries no verdict.
     */
    PUBLICATION_ASSESSMENT_APPROVED: 'publication_assessment_approved',
    // ── Sprint 65.1.0 — compliance obligation coordination (AC9) ──
    //
    // Four codes because AC9 names four moments — trigger, transition, completion
    // and export — and each answers a different question an inspection asks. They
    // are written through `AuditService.log` wrapped in the `issued-records-audit.ts`
    // module shape, deliberately NOT through `maybeIngestToGroupAudit`: Sprint
    // 65.1.0's P0-C measured that function filtering `grc_organizations` on a field
    // `Organization` does not declare, so it returns before its insert for every
    // organisation in the deployment, grouped or not. 77 files import it.
    /**
     * A domain fact created an obligation. The event this cites is immutable and
     * carries its own `trigger_source_field`, so the trail records what the
     * platform read, not only that it reacted.
     */
    COMPLIANCE_OBLIGATION_TRIGGERED: 'compliance_obligation_triggered',
    /**
     * An obligation changed state — including into `blocked`, which is a
     * transition an inspection cares about more than the ordinary ones. The
     * payload keeps the statutory clocks and the recommended cadence distinct
     * (AC4); they are never merged into one due date on the way to the trail.
     */
    COMPLIANCE_OBLIGATION_TRANSITIONED: 'compliance_obligation_transitioned',
    /**
     * An obligation closed against a recorded `ComplianceExecutionProof`. Its own
     * code rather than a transition, because "this duty is discharged and here is
     * what discharged it" is the entry the register exists to produce, and it
     * cannot be reached without a proof.
     */
    COMPLIANCE_OBLIGATION_COMPLETED: 'compliance_obligation_completed',
    /**
     * A work-programme or completeness package was produced through
     * `UnifiedExportService`. On the trail because the export carries obligations,
     * owners and evidence state out of the platform, and who took that and when is
     * the auditor's question about the auditor's own artefact.
     */
    COMPLIANCE_OBLIGATION_EXPORTED: 'compliance_obligation_exported',
    // ── Sprint 65.2.0 — the two writers 65.1.0 left unbuilt ──
    /**
     * A trigger binding was installed for a toolkit row the organisation's
     * installed content declares.
     *
     * Its own code rather than a transition, because it answers a question the
     * other four cannot: *why does this duty exist at all, and against which
     * declared row*. The payload carries the AC2 measurement — how many installed
     * records stating an expressible period the row's selector actually reached —
     * so an auditor asking why a duty has no statutory clock is answered by the
     * entry that created it rather than by re-running the measurement later
     * against content that may have moved.
     */
    COMPLIANCE_BINDING_INSTALLED: 'compliance_binding_installed',
    /**
     * A `ComplianceExecutionProof` was recorded by the domain service that holds
     * the evidence.
     *
     * Separate from `COMPLIANCE_OBLIGATION_COMPLETED`, and the separation is the
     * point: recording a proof and closing a duty are two acts, they can be
     * minutes or weeks apart, and a duty may carry several proofs of which one
     * closes it. One code for both would make "the evidence arrived" and "the
     * obligation is discharged" indistinguishable on the trail — which is exactly
     * the conflation the proof contract exists to prevent.
     */
    COMPLIANCE_EXECUTION_PROOF_RECORDED: 'compliance_execution_proof_recorded',
    /**
     * Sprint 65.4.0 (AC3) — an external submission was prepared.
     *
     * Four codes and not one, on the same reasoning as the proof/completion split
     * above. Preparing, transmitting, hearing back and giving up are four acts
     * with four different meanings to anyone reading the trail afterwards, and
     * they can be days apart. A single `external_action_updated` would make "we
     * decided to file" and "the authority rejected it" indistinguishable, which
     * is precisely what a filing history is read to tell apart.
     */
    COMPLIANCE_EXTERNAL_ACTION_PREPARED: 'compliance_external_action_prepared',
    /**
     * Transmission was asserted (assisted) or performed (provider).
     *
     * Deliberately not called `submitted`: nothing outside this product has said
     * anything at this point, and a code reading as "filed" would be the claim
     * assisted mode is forbidden to make.
     */
    COMPLIANCE_EXTERNAL_ACTION_DISPATCHED: 'compliance_external_action_dispatched',
    /** Something came back and was classified. */
    COMPLIANCE_EXTERNAL_ACTION_RECEIPT_RECORDED: 'compliance_external_action_receipt_recorded',
    /** The operator stopped. Terminal, and honest about never having been filed. */
    COMPLIANCE_EXTERNAL_ACTION_ABANDONED: 'compliance_external_action_abandoned',
    // ── Sprint 65.6.0 — DT-01 training and awareness ──
    /** A course was defined, or its catalogue entry was activated/retired. */
    TRAINING_COURSE_CREATED: 'training_course_created',
    /**
     * A course was assigned to a named person. One entry per assignee, because
     * "who was required to do this" is answered per person and a single entry
     * naming a batch cannot be read back that way.
     */
    TRAINING_ASSIGNED: 'training_assigned',
    /**
     * A completion was recorded, with the evidence it rests on in `new_value`.
     *
     * Its own code rather than a status transition, on the reasoning
     * `COMPLIANCE_EXECUTION_PROOF_RECORDED` gives: "this person completed the
     * training" and "somebody moved a row to done" are the two things the DT-01
     * workflow gap was about, and one code for both would make them
     * indistinguishable on the trail — which is precisely what the
     * `TRAINING_COMPLETION_REQUIRES_EVIDENCE` refusal exists to keep apart.
     */
    TRAINING_COMPLETION_RECORDED: 'training_completion_recorded',
    /**
     * A person was exempted, by a named decider, for a stated reason.
     *
     * Separate from the completion code and deliberately so: an exemption is not
     * a completion, it never counts toward the readiness measurement, and an
     * inspection asking "how many of these were actually done" must be able to
     * tell the two apart without reading a payload field.
     */
    TRAINING_EXEMPTION_RECORDED: 'training_exemption_recorded',
    /** An assignment was withdrawn — the obligation went away, nobody did it. */
    TRAINING_ASSIGNMENT_CANCELLED: 'training_assignment_cancelled',
    // ── Sprint 65.6.0 (AC6) — DPO-01 appointment, PUB-01 public notice ──
    /** An appointment was recorded, with the decision behind it in `new_value`. */
    DPO_APPOINTMENT_RECORDED: 'dpo_appointment_recorded',
    /**
     * The appointment took effect — the organisation now has a named officer.
     *
     * Its own code and not a generic status change, because this is the moment
     * the duty starts running and it is the row an inspection reads to answer
     * "from when". A generic transition code would make it indistinguishable from
     * the appointment ending.
     */
    DPO_APPOINTMENT_TOOK_EFFECT: 'dpo_appointment_took_effect',
    /** The appointment was made public, to the targets in `new_value`. */
    DPO_APPOINTMENT_PUBLISHED: 'dpo_appointment_published',
    /** The appointment ended or was superseded, with its reason. */
    DPO_APPOINTMENT_ENDED: 'dpo_appointment_ended',
    /** A public privacy notice was drafted. */
    PRIVACY_NOTICE_DRAFTED: 'privacy_notice_drafted',
    /** Somebody with the authority signed the text off. */
    PRIVACY_NOTICE_APPROVED: 'privacy_notice_approved',
    /**
     * The notice went public, to the targets in `new_value`.
     *
     * Separate from the approval code and deliberately so: "approved but never
     * actually published" is the failure this row is most often found in, and one
     * code for both acts would make it unreadable off the trail.
     */
    PRIVACY_NOTICE_PUBLISHED: 'privacy_notice_published',
    /** The notice was taken down, with the reason it was taken down for. */
    PRIVACY_NOTICE_WITHDRAWN: 'privacy_notice_withdrawn',
    /** A newer notice replaced it. */
    PRIVACY_NOTICE_SUPERSEDED: 'privacy_notice_superseded',
    // ── Sprint 63.13.0 — the stored metric series ──
    /**
     * A person captured the current period's point of a metric series on demand
     * (`POST /dashboard/metrics/:metric_key/capture`, AC8). The scheduler's
     * hourly writes are not audited per point — the point itself carries
     * `recorded_by: 'scheduler'`; a person's capture is, because it is a write
     * a person chose to make. Written only when the capture's write APPLIED
     * (Gate E12.5 pass 3): a capture handed back a row it did not write — a
     * month already closed, or an insert race lost — writes no audit row.
     */
    METRIC_SERIES_POINT_CAPTURED: 'metric_series_point_captured',
    // ── Sprint 63.25.0 — audit evidence packs (spec F5) ──
    /** Version 1 of a pack was assembled (every source answered, or `partial` follows). */
    AUDIT_EVIDENCE_PACK_ASSEMBLED: 'audit_evidence_pack_assembled',
    /** A later version was assembled from the stored definition. */
    AUDIT_EVIDENCE_PACK_REASSEMBLED: 'audit_evidence_pack_reassembled',
    /** The version just assembled is `partial`: the failed sources are named. */
    AUDIT_EVIDENCE_PACK_PARTIAL: 'audit_evidence_pack_partial',
    /** A person checked what changed in the sources since the version; nothing changed on the pack. */
    AUDIT_EVIDENCE_PACK_DRIFT_CHECKED: 'audit_evidence_pack_drift_checked',
    /** A reviewer who did not assemble the version verified it. */
    AUDIT_EVIDENCE_PACK_VERIFIED: 'audit_evidence_pack_verified',
    /** A reviewer returned the version, with a note. */
    AUDIT_EVIDENCE_PACK_RETURNED: 'audit_evidence_pack_returned',
    /**
     * A person recorded that a verified version was sent. An internal record,
     * not proof that the recipient received or accepted it.
     */
    AUDIT_EVIDENCE_PACK_RECORDED_AS_SUBMITTED: 'audit_evidence_pack_recorded_as_submitted',
    /** A review or submission was refused because the version's status does not allow it. */
    AUDIT_EVIDENCE_PACK_TRANSITION_REFUSED: 'audit_evidence_pack_transition_refused',
    /** A version was exported; the format and version are in `new_value`. */
    AUDIT_EVIDENCE_PACK_EXPORTED: 'audit_evidence_pack_exported',
    /**
     * fix-sprint-20260411-0440-followup F1 — the data-subject profile register.
     *
     * Its five operator writes (create, edit, soft-delete, attach a rights-request
     * case, attach a consent workflow) recorded neither an actor nor an event.
     * Payloads follow `docs/compliance/audit-event-taxonomy.md` §4.1: the profile
     * is named by its `profile_id` and a changed field by its NAME, never by the
     * data subject's name, email, phone or identifiers.
     */
    DATA_SUBJECT_PROFILE_CREATED: 'data_subject_profile_created',
    DATA_SUBJECT_PROFILE_UPDATED: 'data_subject_profile_updated',
    DATA_SUBJECT_PROFILE_DELETED: 'data_subject_profile_deleted',
    /** `previous_value.data_subject_profile_id` names the person the case was attached to before, or null. */
    DATA_SUBJECT_CASE_LINKED: 'data_subject_case_linked',
    /** `previous_value.data_subject_profile_id` names the person the workflow was attached to before, or null. */
    DATA_SUBJECT_WORKFLOW_LINKED: 'data_subject_workflow_linked',
    /**
     * Sprint 63.31.0 (#415, owner ruling Q3 (b), decision E8) — creating an issued
     * record for a linked profile recorded the record's subject identifier under
     * the kind's declared key (`details`: the key and the issued record id, never
     * the value).
     */
    DATA_SUBJECT_EXTERNAL_IDENTIFIER_RECORDED: 'data_subject_external_identifier_recorded',
    /**
     * Sprint 63.31.0 (E8) — the profile already held a DIFFERENT value under the
     * key; it was kept and the conflict recorded on the profile (no value copied).
     */
    DATA_SUBJECT_EXTERNAL_IDENTIFIER_CONFLICT: 'data_subject_external_identifier_conflict',
    // ── Sprint 62.53.0 — organisation-profile statements (#665, #662) ──
    /**
     * A person recorded a revision of a recorded organisation-profile attribute
     * (`PATCH /org-settings/profile-statements/:attribute`). Entity
     * `org_profile_statement`; `previous_value` and `new_value` each carry
     * `{ state, values, evidence_file_ids, revision }`, so the trail alone says
     * what the organisation stated before and after.
     */
    ORG_PROFILE_STATEMENT_RECORDED: 'org_profile_statement_recorded',
    // ── Sprint 62.57.0 (Slice R) — authority communications on a filing ──
    /**
     * A person recorded an authority's written communication on a dossier filing
     * (`POST /assessments/dossier-filings/:filingId/authority-communications`).
     * Entity `dossier_authority_communication`. The row carries the kind, the
     * receipt date and the NAMES of the fields recorded; the free text (sender,
     * wording, items, period, notes, correction note) is withheld (AC-X6).
     */
    DOSSIER_AUTHORITY_COMMUNICATION_RECORDED: 'dossier_authority_communication_recorded',
    /** One filing's communication history exported (PDF or XLSX) through `UnifiedExportService`. */
    DOSSIER_AUTHORITY_COMMUNICATION_EXPORTED: 'dossier_authority_communication_exported',
    // ── Sprint 62.58.0 (Slice C) — the framework summary and briefing record ──
    /**
     * A framework's summary exported through `UnifiedExportService`
     * (`POST /localization/frameworks/:id/summary-export`, AC-C1; the spec's
     * `framework.summary_exported`). Entity `framework_summary_export`; the row
     * carries the framework code, the format, the locale and the pack version.
     */
    FRAMEWORK_SUMMARY_EXPORTED: 'framework_summary_exported',
    /**
     * A briefing or consultation comment recorded on a framework (AC-C3).
     * Entity `framework_briefing`. The row carries the kind, the day, the
     * channel, the stamped framework facts and the NAMES of the fields recorded;
     * the free text (audience, summary, channel note, reference, correction
     * note) is withheld (AC-X6).
     */
    FRAMEWORK_BRIEFING_RECORDED: 'framework_briefing_recorded',
    /** The named approver approved a briefing entry, once (AC-C3). Entity `framework_briefing`. */
    FRAMEWORK_BRIEFING_APPROVED: 'framework_briefing_approved',
    // ── Sprint 62.57.0 (Slices A, B and the Slice F stamp) ──
    /**
     * The authority's receipt of a dossier recorded (or corrected) on a filing,
     * and the governing text re-stamped from it (D17). Entity `dossier_filing`.
     */
    DOSSIER_RECEIPT_EVIDENCE_RECORDED: 'dossier_receipt_evidence_recorded',
    /**
     * A person's statement of which body handles a dossier, citing a recorded
     * communication (AC-A2). Entity `dossier_handling_authority_statement`; the
     * body's name and the correction note are withheld (AC-X6).
     */
    DOSSIER_HANDLING_STATEMENT_RECORDED: 'dossier_handling_statement_recorded',
    /** An appraisal result recorded from a pack option (AC-B7). Entity `dossier_filing`; notes withheld. */
    DOSSIER_RESULT_RECORDED: 'dossier_result_recorded',
    /** A concrete supplement request after a `passed_supplement_required` result (AC-B3). Items withheld. */
    DOSSIER_RESULT_SUPPLEMENT_REQUEST_RECORDED: 'dossier_result_supplement_request_recorded',
    /** A revision of the follow-up a `failed` result opens (AC-B6). Reasons and remediation withheld. */
    DOSSIER_FAILURE_FOLLOW_UP_RECORDED: 'dossier_failure_follow_up_recorded',
    /** A result date the authority itself states (D20). */
    DOSSIER_AUTHORITY_STATED_RESULT_DATE_RECORDED: 'dossier_authority_stated_result_date_recorded',
    /** Sprint 62.57.0 (Slice C, D25) — a completion request citing a recorded communication. Notes withheld. */
    DOSSIER_COMPLETION_REQUEST_RECORDED: 'dossier_completion_request_recorded',
    /** Sprint 62.57.0 (Slice C, D30) — a completion submitted after a period opened. Notes withheld. */
    DOSSIER_COMPLETION_SUBMITTED: 'dossier_completion_submitted',
    /** Sprint 62.57.0 (Slice D, D35) — an update filing created from its original. */
    DOSSIER_UPDATE_FILING_CREATED: 'dossier_update_filing_created',
    /** Sprint 62.57.0 (Slice D, D36) — the update's recipient and competent authority. Names and reason withheld. */
    DOSSIER_UPDATE_ADDRESSEE_RECORDED: 'dossier_update_addressee_recorded',
    // ── Sprint 62.58.0 Slice F (AC-F4) ──
    /**
     * An organisation's reading of a regulatory update's requirement comparison
     * recorded (`POST /localization/regulatory-updates/:updateId/comparison-reviews`).
     * Entity `regulatory_update`. The row carries the names of the fields
     * recorded and the count of readings; the conclusion, the next steps and
     * every note are withheld (AC-X6).
     */
    REGULATORY_UPDATE_COMPARISON_REVIEWED: 'regulatory_update_comparison_reviewed',
    // ── Sprint 62.58.0 Slice N (AC-N3) ──
    /**
     * A revision of a processing activity's deployment access statement recorded
     * (`POST /data-mapping/activities/:id/access-statements`). Entity
     * `access_statement`. The row carries the version, the version replaced, the
     * day reviewed, the approver, the systems, each entry's access kinds and the
     * NAMES of the fields recorded; the free text (roles or positions, means,
     * conditions) is withheld (AC-X6).
     */
    ACCESS_STATEMENT_RECORDED: 'access_statement_recorded',
};
/**
 * Sprint 62.46.0 (gate E12.5 pass 1, P1-3) — the audit event types that
 * describe a suspicious-activity case. A reader of the audit trail who does
 * not hold `aml_case:read` is not shown them: the case register answers such a
 * caller an identical 404, and a trail listing when a case was opened,
 * determined or approved would be the register's existence by another door.
 * Platform event codes only.
 */
export const AML_CASE_AUDIT_EVENT_TYPES = [
    AuditActionType.AML_CASE_OPENED,
    AuditActionType.AML_CASE_UPDATED,
    AuditActionType.AML_CASE_VIEWED,
    AuditActionType.AML_CASE_LISTED,
    AuditActionType.AML_CASE_READ_DENIED,
    AuditActionType.AML_CASE_DETERMINED,
    AuditActionType.AML_CASE_APPROVED,
    AuditActionType.AML_CASE_SELF_APPROVAL_REFUSED,
    AuditActionType.AML_CASE_APPROVAL_REVERTED,
    AuditActionType.AML_CASE_FILING_RECORDED,
    AuditActionType.AML_CASE_CLOSED,
    AuditActionType.AML_CASE_CDD_REFRESH_REQUESTED,
    AuditActionType.AML_CASE_RELATED_ACTION_RECORDED,
    AuditActionType.AML_CASE_RETENTION_START_RECORDED,
];
/**
 * Sprint 62.46.0 (gate E12.5 pass 2, P1 b) — every audit event type a reader
 * without `aml_case:read` is not shown: the case register's own events
 * ({@link AML_CASE_AUDIT_EVENT_TYPES}) and the inspection-pack export, whose
 * row records that an extract of the case register was taken. The export's
 * row no longer carries case ids or a filing-record count, but its existence
 * in a period beside the pack's own attachment is still the register's by
 * another door, so it is restricted with the case events. Consumed by
 * `restrictedAuditEventTypesFor` (backend), the group-sink exclusion below and
 * the outbound proof mask, so the three cannot drift apart.
 */
export const AML_RESTRICTED_AUDIT_EVENT_TYPES = [
    ...AML_CASE_AUDIT_EVENT_TYPES,
    AuditActionType.AML_INSPECTION_PACK_EXPORTED,
    AuditActionType.AML_CASE_READS_RETENTION_SWEPT,
];
/**
 * Sprint 62.46.0 (gate E12.5 pass 2) — event types that are NEVER ingested
 * into the group audit sink (`grc_group_audit_consolidated`).
 *
 * The group sink is read with `group:audit:read` across every member
 * organisation of a group, and no reader of it can check the org-local grant
 * that decides who may learn a suspicious-activity case exists
 * (`aml_case:read`). The confidentiality of that work is the reporting
 * entity's own, so the case register's events stay in the entity's own trail
 * (`grc_case_audit_events`, filtered per reader by
 * `restrictedAuditEventTypesFor`) and are not aggregated for the group.
 *
 * Derived, not restated: a new case event added to
 * {@link AML_RESTRICTED_AUDIT_EVENT_TYPES} is excluded here by construction.
 * The AML programme's OTHER events (scoping, indicators, beneficial owners,
 * launches) are not case events and still reach the group sink; the
 * inspection-pack export does not (gate E12.5 pass 2, P1 b). Consulted by `createModuleAuditWriter` and, as a backstop
 * for every direct caller, by `maybeIngestToGroupAudit`.
 */
export const GROUP_AUDIT_EXCLUDED_EVENT_TYPES = [
    ...AML_RESTRICTED_AUDIT_EVENT_TYPES,
];
/**
 * Audit event actor types.
 */
export const ActorType = {
    USER: 'user',
    SYSTEM: 'system',
};
/**
 * Well-known system actor IDs.
 * See: docs/compliance/audit-event-taxonomy.md - Section 4.3
 */
export const SystemActorId = {
    INTAKE: 'system:intake',
    SLA_ENGINE: 'system:sla-engine',
    EXPORT_JOB: 'system:export-job',
    CMP_GATEWAY: 'system:cmp-gateway',
    NOTIFICATION: 'system:notification',
    /**
     * Sprint 64.38.0 — a startup migration that changed compliance state.
     *
     * A boot path has no session and no user, and attributing its writes to a real
     * person would be the worst available outcome: the audit trail would name
     * somebody who was not there. `actor_role` is recorded as the migration key,
     * so "which migration did this?" is answerable from the row itself rather than
     * from a log line that has since rotated.
     */
    MIGRATION: 'system:migration',
};
/**
 * Event sensitivity levels.
 * See: docs/compliance/audit-event-taxonomy.md - Section 5
 */
export const EventSensitivity = {
    PUBLIC: 'public',
    INTERNAL: 'internal',
    SENSITIVE: 'sensitive',
    RESTRICTED: 'restricted',
};
// ── Sprint 43.0.0 — audit payload enrichments (E6) ────────────────
//
// Runtime-value mirrors of the type unions exported from
// `types/case.ts` (`PauseReason`, `RejectionReasonCode`). The types
// in `case.ts` give compile-time types; the constants below give
// runtime values for use in Zod enum validators, frontend select
// option lists, and audit payload assembly.
/**
 * Pause reason codes for `sla_paused` audit events.
 * See: docs/compliance/audit-event-taxonomy.md §3.2
 */
export const PauseReasonCode = {
    AWAITING_SUBJECT: 'awaiting_subject',
    AWAITING_VERIFICATION: 'awaiting_verification',
    THIRD_PARTY_DEPENDENCY: 'third_party_dependency',
    OTHER: 'other',
};
/**
 * Rejection reason codes for `case_rejected` audit events.
 * See: docs/compliance/audit-event-taxonomy.md §3.1
 */
export const RejectionReasonCodeEnum = {
    IDENTITY_NOT_VERIFIED: 'identity_not_verified',
    MANIFESTLY_UNFOUNDED: 'manifestly_unfounded',
    REPETITIVE: 'repetitive',
    EXEMPT: 'exempt',
    OUT_OF_SCOPE: 'out_of_scope',
    DUPLICATE: 'duplicate',
};
//# sourceMappingURL=audit.js.map